import { useReducer, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { isBlocked } from '../outline/blocked.js'
import { sectionKey, itemKey, getFirst, getMemberName, showPill } from '../outline/utils.js'
import { SHEET_PHASE_MAP, SHEET_TYPE_MAP, resolveMilestonePhasePrefix, CONCEPT_BRANCH_MS_TYPE_IDS, CONCEPTUAL_FRAMEWORK_TYPE_ID, INTEGRATED_DESIGN_PROPOSAL_TYPE_ID, CRITERIA_BRANCH_MS_TYPE_IDS, BIM_MS_TYPE_IDS, BIM_MS_TYPE_PHASE, PERMIT_MS_TYPE_IDS, BID_CAMPAIGN_MS_TYPE_ID, BID_ADDENDUM_MS_TYPE_ID, VALUE_ENGINEERING_MS_TYPE_ID, SPEC_RELEASE_MS_TYPE_IDS, SCHEDULE_RELEASE_MS_TYPE_IDS, isAsiMilestoneType, isRfiMilestoneType, isSubmittalMilestoneType, DOCS_HIDDEN_MS_TYPE_IDS, DOCS_UPLOAD_LOCKED_MS_TYPE_IDS, POST_REVISION_TYPE_IDS, DAPC_DRAWINGS_MS_TYPE_ID, RENDERING_PACKAGE_TYPE_ID, PRE_DESIGN_RESEARCH_TYPE_ID, EXISTING_BUILDING_3D_MODEL_TYPE_ID, selectionsDocKinds, SELECTIONS_DOC_LABELS } from '../outline/constants.js'
import MilestoneAsiMount from './MilestoneAsiMount.jsx'
import MilestoneRfiMount from './MilestoneRfiMount.jsx'
import MilestoneSubmittalMount from './MilestoneSubmittalMount.jsx'
import DrawingSheetList, { getDiscipline, DISCIPLINE_ORDER, SheetRow, openQcTab } from './DrawingSheetList.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { getMilestoneChecklistItems, getProjectSheetChecklistItems } from '../services/checklist.js'
import { getConcepts, getOrganizations, getCriteria } from '../services/design.js'
import { getPassengersFor } from '../services/passengers.js'
import { getThreadById } from '../services/threads.js'
import ThreadNode from './ThreadNode.jsx'
import { getParcels } from '../services/parcels.js'
import { ParcelRow, ConceptRow, ConceptsList, CriteriaCategoryRows, criteriaAnsweredTotal } from './DesignSection.jsx'
import { getMilestoneSheetRevisions, getSheetSlackTimestamps, getProjectSheets } from '../services/sheets.js'
import { getProjectBimSystems } from '../services/bim.js'
import { BimRow } from './BimSystemList.jsx'
import { getMilestoneDocuments, uploadDocument, deleteDocument, deleteLegacyDocument, updateDocument, markDocumentDone, unlockDocument } from '../services/documents.js'
import { completeSelectionsDoc } from '../services/selectionsDocuments.js'
import { getHubEntriesForMilestone, deleteEntry as deleteJourneyEntry } from '../services/journey.js'
import { openDocQcTab, makePresentationReviewCopy } from '../services/documentMarkup.js'
import { getIntegratedDocs } from './milestoneDocumentProviders.js'
import WorkingNotesSection from './WorkingNotesSection.jsx'
import KeyDatesSection from './KeyDatesSection.jsx'
import MilestoneGateSection from './MilestoneGateSection.jsx'
import MilestoneFeederSection from './MilestoneFeederSection.jsx'
import { PermitNode } from './PermitsSection.jsx'
import { getBidCampaignForMilestone, bidAdjustment } from '../services/bidCampaigns.js'
import { getEffortsForMilestone, sumAcceptedSavings, VE_STATUS_LABELS } from '../services/valueEngineering.js'
import { getMilestoneSpecVersions } from '../services/specSections.js'
import { getMilestoneScheduleItems } from '../services/schedules.js'
import { scheduleSpecFor, scheduleCell } from '../services/scheduleLayout.js'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

/* ── Milestone section zoom helper ──────────────────────────────────────── */
// All milestone section zoom navigations route through this helper so that:
//   1. wf.zoomMsId is always set — required for MilestoneSections' zoom mode
//   2. wf.zoomPhaseId is derived from the milestone record if not already set —
//      required for the outline engine's buildBreadcrumb() to show the phase segment
//   3. ctx.render() is called instead of dispatching outline-render directly —
//      ctx.render() rebuilds #aj-sc-chrome (the breadcrumb trail) before dispatching
function navigateToMsSection(msId, sectionKey) {
  wf.zoomMsId    = msId
  wf.zoomSection = sectionKey
  // Derive phase ID for breadcrumb if not already in view state
  if (!wf.zoomPhaseId) {
    const msRec = (ctx.state?.milestones || []).find(m => m.id === msId)
    const phId  = (msRec?.fields?.['Phase'] || [])[0]
    if (phId) wf.zoomPhaseId = phId
  }
  saveViewState()
  // ctx.render = render is wired in initOutline (outline.js line 147).
  // render() rebuilds the chrome breadcrumb bar then dispatches outline-render.
  if (ctx.render) ctx.render()
  else window.dispatchEvent(new Event('outline-render'))
}

/* ── Client-side sheet sort: sort order ascending (string compare), nulls last, then sheet number ── */
function clientSheetSort(a, b) {
  const sortVal = s => (getFirst(s.fields['Sheet Sort Order (from Drawing Sheet)']) || '').trim()
  const numTitle = s => (getFirst(s.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim()
  const ka = sortVal(a), kb = sortVal(b)
  if (!ka && !kb) return numTitle(a).localeCompare(numTitle(b), undefined, { numeric: true, sensitivity: 'base' })
  if (!ka) return 1
  if (!kb) return -1
  return ka.localeCompare(kb, undefined, { numeric: true, sensitivity: 'base' }) || numTitle(a).localeCompare(numTitle(b), undefined, { numeric: true, sensitivity: 'base' })
}

// Milestone types that are administrative submission events, not drawing-production milestones.
// Drawing Sheets is suppressed for these even when milestonePhasePrefix resolves via the phase map
// (sheets still show if revisions are already attached to the milestone).
// 2026-09-22: converted from a prefix-string match (to tolerate a trailing emoji on the Airtable
// Name) to a plain id match — id-based matching needs no such tolerance, since it doesn't break
// on a rename at all.
const NO_SHEETS_MS_TYPE_IDS = new Set([
  'recMed65PtHVDmxpc', // PPC Initial Agency Submission 🔹
  'recXhNJUfXhYgYDew', // DAPC Initial Agency Submission 🔹
])
const isNoSheetsMsType = id => NO_SHEETS_MS_TYPE_IDS.has(id)

// The drawing-release maps + resolver moved to outline/constants.js (2026-10-26) so AlderCAD's
// send-to-milestone checklist gate reads the same rule without importing this file.
export { SHEET_PHASE_MAP, SHEET_TYPE_MAP, resolveMilestonePhasePrefix }

// Muted outline-row filler (loading / empty states inside a section's children).
function msSheetFiller(text) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
        <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>{text}</span></div>
      </div>
    </div>
  )
}

function fmtDate(d) {
  if (!d) return ''
  const p = d.split('-')
  return MONTHS[parseInt(p[1]) - 1] + ' ' + parseInt(p[2])
}

/* ── Generic section node — mirrors buildSectionNode in builders.js ────── */
/*
 * Optional `menuContent` prop: when provided, the ⋯ button uses the React
 * ActionMenu portal (same module as DisciplineGroup and SheetRow) instead of
 * the outline engine's data-fn="openMenu" HTML menu. This gives visual parity
 * across all section levels.
 */
function SectionNode({ msId, type, label, count, progress, children, menuContent }) {
  const key = sectionKey(msId, type)
  const isCollapsed = wf.collapsed[key] !== false // default collapsed
  const menu = useActionMenu()  // always called (hooks must be unconditional)
  // Optional aggregate progress (e.g. the Checklist header's done/total across all categories) —
  // same bar treatment as CategoryNode, one level up. Falls back to a bare count when absent.
  const pct = progress && progress.total > 0 ? Math.round(progress.done / progress.total * 100) : 0
  const barColor = pct === 100 ? '#1D9E75' : pct > 0 ? '#1a6fa8' : 'var(--aj-border)'
  return (
    <div
      className={`wf-node wf-node--section wf-node--section-${type}${isCollapsed ? ' wf-node--collapsed' : ''}`}
      data-key={key}
    >
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--section"
          data-fn="zoomTo"
          data-level="section"
          data-sectionkey={key}
          title={`Focus on ${label}`}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          {progress ? (
            <span className="wf-meta">
              <span className="wf-cat-bar-wrap">
                <span className="wf-cat-bar-fill" style={{ width: pct + '%', background: barColor }}></span>
              </span>
              <span className="wf-cat-count">{progress.done}/{progress.total}</span>
            </span>
          ) : (
            count != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: '8px' }}>{count}</span>
          )}
        </div>
        {menuContent ? (
          /* React ActionMenu — same module as DisciplineGroup, SheetRow, etc. */
          <ActionMenuButton menu={menu} />
        ) : (
          /* Outline engine menu — used for section types without custom React menu */
          <button
            className="wf-menu-btn"
            data-fn="openMenu"
            data-type="section"
            data-key={key}
            data-sectiontype={type}
            data-msid={msId}
          >&#8943;</button>
        )}
      </div>
      {/* ActionMenu portal — only rendered when menuContent is provided */}
      {menuContent && <ActionMenu menu={menu}>{menuContent}</ActionMenu>}
      {!isCollapsed && <div className="wf-children">{children}</div>}
    </div>
  )
}

/* ── Threads riding this milestone (docs/PASSENGERS.md) ──────────────────────
   Rendered with the canonical ThreadNode (One-Component-Per-Node-Type) so a passenger looks and
   behaves exactly like a thread anywhere else — same triangle/expand, activity log, status, ⋯ menu.
   getPassengersFor only returns the slim pairing row, so this resolves each one to its full Airtable
   thread record before caching. */
function loadMsPassengerThreads(msId, dispatch) {
  if (!ctx.msPassengerCache) ctx.msPassengerCache = {}
  if (ctx.msPassengerCache[msId] !== undefined) return
  ctx.msPassengerCache[msId] = null
  getPassengersFor([msId])
    .then(r => {
      const rows = (r.byMilestone && r.byMilestone[msId]) || []
      if (!rows.length) { if (ctx.msPassengerCache) { ctx.msPassengerCache[msId] = []; dispatch() } ; return null }
      return Promise.all(rows.map(row => getThreadById(row.threadId))).then(threads => {
        if (!ctx.msPassengerCache) return
        ctx.msPassengerCache[msId] = threads.filter(Boolean)
        dispatch()
      })
    })
    .catch(() => { if (ctx.msPassengerCache) { ctx.msPassengerCache[msId] = []; dispatch() } })
}

/** Reload the riding lists of these milestones after a thread moves (MoveThreadsModal, the post
    window). Clearing the cache alone is not enough: the inline lazy-load ref has already fired. */
export function refreshMsPassengers(msIds) {
  const render = () => window.dispatchEvent(new Event('outline-render'))
  for (const id of new Set((msIds || []).filter(Boolean))) {
    if (ctx.msPassengerCache) delete ctx.msPassengerCache[id]
    loadMsPassengerThreads(id, render)
  }
}

/* ── Spec version row (shared by inline section + section zoom) ───────────── */
const SPEC_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function specFmtDate(d) { if (!d) return ''; const p = d.split('-'); return SPEC_MONTHS[parseInt(p[1]) - 1] + ' ' + parseInt(p[2]) }
function specVersionRowNode(v) {
  const sec = v.spec_sections || {}
  return (
    <div key={v.id} className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{sec.section_number} — {sec.section_name}</span>
          <span className="wf-meta" style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11 }}>
            v{v.version_number}{v.issued_date ? ' · ' + specFmtDate(v.issued_date) : ''}{v.release_mode ? ' · ' + v.release_mode : ''}
          </span>
          {v.pdf_url ? <a href={v.pdf_url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 10, fontSize: 11, color: '#2563eb' }}>PDF ↗</a> : null}
        </div>
      </div>
      {v.changes_summary ? (
        <div className="wf-children">
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <span className="wf-bullet wf-bullet--item" />
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-mid)', fontSize: 11 }}>Changes: {v.changes_summary}</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/* ── Schedule membership row (item in a milestone — shared by inline + zoom) ─── */
// Renders one schedule item that belongs to a milestone, showing its draft/issued state. The item
// row carries `version` (the milestone version). Editing happens in the Schedule Editor (⋯ menu).
function scheduleMembershipRowNode(it) {
  const spec = scheduleSpecFor(it.schedule_type)
  const hint = spec.columns[1] ? scheduleCell(it, spec.columns[1][1]) : ''
  const ver = it.version || {}
  const isOpen = ver.status === 'open'
  return (
    <div key={it.id} className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{it.item_label || '(unnamed)'}</span>
          <span className="wf-meta">
            <span style={{ fontSize: 10, color: 'var(--aj-text-muted)' }}>{it.schedule_type}</span>
            {hint && <span style={{ fontSize: 10, color: 'var(--aj-text-muted)' }}>{hint}</span>}
            <span style={{ fontSize: 10, fontFamily: '"DM Sans",sans-serif', fontWeight: 600, color: isOpen ? '#c79a3a' : '#1D9E75' }}>
              {isOpen ? 'Draft v' + ver.version_number : 'Issued v' + ver.version_number}
            </span>
          </span>
        </div>
      </div>
    </div>
  )
}

/* ── Document helpers ────────────────────────────────────────────────────── */
// The colored file-type pill (PDF/DOC/XLS/…) this used to also compute was removed — it broke
// the outline's graphical standard (every other row uses plain text + the bullet/dot; a colored
// chip was the only place that pattern was violated). isLink is still needed to pick the right
// action-menu label ("Open link" vs "Open / download").
function docIsLink(doc) {
  const ext = (doc.file_type || '').toLowerCase()
  return doc.source === 'link' || ext === 'link'
    || (doc._legacy && !['pdf','docx','doc','xlsx','xls','pptx','ppt'].includes(ext))
}

/* ── Milestone document upload modal (mirrors CoordDocUploadModal) ────────── */
function MilestoneDocUploadModal({ msId, onClose, onDone, initialTitle = '', source }) {
  const [title, setTitle]   = useState(initialTitle)
  const [file,  setFile]    = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError]   = useState(null)
  const fileRef = useRef()

  async function handleSubmit(e) {
    e.preventDefault()
    if (!file) { setError('Please select a file.'); return }
    setUploading(true); setError(null)
    try {
      const doc = await uploadDocument({ milestoneId: msId, file, name: (title || '').trim() || file.name, source })
      onDone(doc)
    } catch (err) {
      setError(err.message || 'Upload failed.')
      setUploading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--aj-bg-surface, #fff)', borderRadius: 10, padding: '24px 28px', minWidth: 360, maxWidth: 480, width: '100%', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 15, marginBottom: 18, color: 'var(--aj-text, #07272D)' }}>Add Document</div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: 0.65, color: 'var(--aj-text, #07272D)', fontFamily: 'DM Sans, sans-serif' }}>Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Defaults to the file name"
              style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text, #07272D)' }}
            />
          </div>
          <div style={{ marginBottom: 18 }}>
            <input
              ref={fileRef}
              type="file"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files[0]
                if (!f) return
                setFile(f)
                if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''))
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current.click()}
              style={{ padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, cursor: 'pointer', background: 'transparent', color: 'var(--aj-text, #07272D)' }}
            >{file ? file.name : 'Choose file…'}</button>
          </div>
          {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 12, fontFamily: 'DM Sans, sans-serif' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '7px 18px', borderRadius: 4, border: '0.5px solid var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, cursor: 'pointer', background: 'transparent', color: 'var(--aj-text, #07272D)' }}
            >Cancel</button>
            <button type="submit" disabled={uploading}
              style={{ padding: '7px 18px', borderRadius: 4, border: 'none', fontFamily: 'DM Sans, sans-serif', fontSize: 13, cursor: uploading ? 'default' : 'pointer', background: '#07272D', color: '#fff', opacity: uploading ? 0.6 : 1 }}
            >{uploading ? 'Uploading…' : 'Add Document'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ── Shared image drop zone — thumbnail preview + click/drag/drop target ──── */
// Used in both BatchRenderingUploadModal (per-row) and EditRenderingDocModal.
// `file`       — new local File (takes priority over currentUrl for preview)
// `currentUrl` — existing image URL shown when no new file is selected yet
// `onFile`     — called with a File whenever the user picks one
function ImageDropZone({ file, currentUrl, onFile, disabled = false }) {
  const [preview, setPreview] = useState(currentUrl || null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [hover, setHover]     = useState(false)
  const fileRef = useRef()

  useEffect(() => {
    if (!file) { setPreview(currentUrl || null); return }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file, currentUrl])

  function handleFiles(files) {
    const img = Array.from(files).find(f => f.type.startsWith('image/'))
    if (img && !disabled) onFile(img)
  }

  const hasImage = Boolean(preview)
  return (
    <div
      onClick={() => !disabled && fileRef.current.click()}
      onDragOver={e => { e.preventDefault(); if (!disabled) setIsDragOver(true) }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false) }}
      onDrop={e => { e.preventDefault(); setIsDragOver(false); handleFiles(e.dataTransfer.files) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderRadius: 6,
        border: `0.5px ${isDragOver ? 'solid var(--aj-text)' : 'dashed var(--aj-border-mid)'}`,
        background: isDragOver ? 'rgba(0,0,0,0.04)' : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: hasImage ? 0 : 54,
        transition: 'border-color 0.1s, background 0.1s',
      }}
    >
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} disabled={disabled}
        onChange={e => { const f = e.target.files[0]; if (f) handleFiles([f]) }} />
      {hasImage ? (
        <>
          <img src={preview} alt="" style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 160 }} />
          {!disabled && (hover || isDragOver) && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.52)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'DM Sans, sans-serif', fontSize: 12 }}>
              {isDragOver ? 'Drop to replace' : 'Click or drop to replace'}
            </div>
          )}
        </>
      ) : (
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: isDragOver ? 'var(--aj-text)' : 'var(--aj-text-muted)', padding: '0 16px', textAlign: 'center' }}>
          {isDragOver ? 'Drop to add' : 'Drop, paste or click'}
        </span>
      )}
    </div>
  )
}

/* ── Batch rendering upload modal (Rendering Package milestones) ─────────── */
// Supports drag-and-drop onto the modal container, paste (images from clipboard),
// and per-row click-to-pick. Each row shows a thumbnail preview once a file is selected.
function BatchRenderingUploadModal({ msId, onClose, onDone }) {
  const [rows, setRows]         = useState([{ id: 1, title: '', file: null }])
  const [statuses, setStatuses] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [modalDragOver, setModalDragOver] = useState(false)
  const nextIdRef = useRef(2)
  const addFilesRef = useRef()

  function addFiles(files) {
    const imgFiles = Array.from(files).filter(f => f.type.startsWith('image/'))
    if (imgFiles.length === 0) return
    setRows(current => {
      const updated = [...current]
      const newRows = []
      for (const file of imgFiles) {
        const emptyIdx = updated.findIndex(r => !r.file)
        if (emptyIdx >= 0) {
          updated[emptyIdx] = { ...updated[emptyIdx], file, title: updated[emptyIdx].title || file.name.replace(/\.[^/.]+$/, '') }
        } else {
          newRows.push({ id: nextIdRef.current++, title: file.name.replace(/\.[^/.]+$/, ''), file })
        }
      }
      return [...updated, ...newRows]
    })
  }
  addFilesRef.current = addFiles

  function addRow() {
    setRows(r => [...r, { id: nextIdRef.current++, title: '', file: null }])
  }
  function removeRow(id) {
    setRows(r => r.filter(x => x.id !== id))
    setStatuses(s => { const n = { ...s }; delete n[id]; return n })
  }
  function setRowTitle(id, title) {
    setRows(r => r.map(x => x.id === id ? { ...x, title } : x))
  }
  function setRowFile(id, file) {
    setRows(r => r.map(x => x.id === id ? { ...x, file, title: x.title || file.name.replace(/\.[^/.]+$/, '') } : x))
  }

  // Global paste — adds clipboard images to empty rows or creates new rows
  useEffect(() => {
    function onPaste(e) {
      if (submitting) return
      const items = Array.from(e.clipboardData?.items || [])
      const imgFiles = items.filter(i => i.type.startsWith('image/')).map(i => i.getAsFile()).filter(Boolean)
      if (imgFiles.length > 0) { e.preventDefault(); addFilesRef.current(imgFiles) }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [submitting])

  async function handleSubmit(e) {
    e.preventDefault()
    const toUpload = rows.filter(r => r.file)
    if (toUpload.length === 0) return
    setSubmitting(true)
    const uploaded = []
    for (const row of toUpload) {
      setStatuses(s => ({ ...s, [row.id]: 'uploading' }))
      try {
        const doc = await uploadDocument({ milestoneId: msId, file: row.file, name: (row.title || '').trim() || row.file.name })
        setStatuses(s => ({ ...s, [row.id]: 'done' }))
        uploaded.push(doc)
      } catch (err) {
        setStatuses(s => ({ ...s, [row.id]: { error: err.message || 'Upload failed' } }))
      }
    }
    setSubmitting(false)
    if (uploaded.length > 0) onDone(uploaded)
  }

  const allHaveFiles   = rows.every(r => r.file)
  const anyUploading   = Object.values(statuses).includes('uploading')
  const readyCount     = rows.filter(r => r.file).length

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{ background: 'var(--aj-bg-surface, #fff)', borderRadius: 10, padding: '24px 28px', minWidth: 400, maxWidth: 540, width: '100%', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: '85vh', overflowY: 'auto', outline: modalDragOver ? '2px solid var(--aj-text)' : 'none', transition: 'outline 0.1s' }}
        onDragOver={e => { e.preventDefault(); if (!submitting) setModalDragOver(true) }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setModalDragOver(false) }}
        onDrop={e => { e.preventDefault(); setModalDragOver(false); if (!submitting) addFiles(e.dataTransfer.files) }}
      >
        <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 15, marginBottom: 4, color: 'var(--aj-text, #07272D)' }}>Add Renderings</div>
        <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 18 }}>Drop images onto this window, paste from clipboard, or click each row to choose a file.</div>
        <form onSubmit={handleSubmit}>
          {rows.map((row, idx) => {
            const st = statuses[row.id]
            const done = st === 'done'
            const uploading = st === 'uploading'
            const hasError = st && typeof st === 'object' && st.error
            const rowDisabled = done || uploading
            return (
              <div key={row.id} style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 6, border: `0.5px solid ${done ? '#86efac' : hasError ? '#fca5a5' : 'var(--aj-border, #c0d0d4)'}`, background: done ? '#f0fdf4' : hasError ? '#fef2f2' : 'transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--aj-text-muted)', minWidth: 16 }}>{idx + 1}</span>
                  <input
                    type="text"
                    value={row.title}
                    onChange={e => setRowTitle(row.id, e.target.value)}
                    placeholder="Title (optional)"
                    disabled={rowDisabled}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, background: rowDisabled ? 'var(--aj-bg-page, #f4f7f8)' : 'var(--aj-bg-subtle)', color: 'var(--aj-text, #07272D)' }}
                  />
                  {rows.length > 1 && !rowDisabled && (
                    <button type="button" onClick={() => removeRow(row.id)} style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>✕</button>
                  )}
                </div>
                <ImageDropZone file={row.file} onFile={f => setRowFile(row.id, f)} disabled={rowDisabled} />
                {(uploading || done || hasError) && (
                  <div style={{ marginTop: 6, fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: uploading ? 'var(--aj-text-muted)' : done ? '#16a34a' : '#dc2626' }}>
                    {uploading ? 'Uploading…' : done ? '✓ Uploaded' : st.error}
                  </div>
                )}
              </div>
            )
          })}
          <button type="button" onClick={addRow} disabled={submitting}
            style={{ display: 'block', marginBottom: 20, padding: '5px 12px', borderRadius: 4, border: '0.5px dashed var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, cursor: 'pointer', background: 'transparent', color: 'var(--aj-text-muted)' }}
          >+ Add another rendering</button>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={anyUploading}
              style={{ padding: '7px 18px', borderRadius: 4, border: '0.5px solid var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, cursor: 'pointer', background: 'transparent', color: 'var(--aj-text, #07272D)' }}
            >Cancel</button>
            <button type="submit" disabled={submitting || !allHaveFiles || anyUploading}
              style={{ padding: '7px 18px', borderRadius: 4, border: 'none', fontFamily: 'DM Sans, sans-serif', fontSize: 13, cursor: (submitting || !allHaveFiles) ? 'default' : 'pointer', background: '#07272D', color: '#fff', opacity: (submitting || !allHaveFiles) ? 0.5 : 1 }}
            >{submitting ? 'Uploading…' : readyCount > 0 ? `Upload ${readyCount} rendering${readyCount === 1 ? '' : 's'}` : 'Upload renderings'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ── Edit rendering document modal ─────────────────────────────────────────── */
// Lets the user rename a Rendering Package document and/or swap its image.
// Paste support: pasting an image anywhere in the modal replaces the selected file.
function EditRenderingDocModal({ doc, msId, onClose, onSaved }) {
  const [title, setTitle]     = useState(doc.name || '')
  const [newFile, setNewFile] = useState(null)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)

  useEffect(() => {
    function onPaste(e) {
      if (saving) return
      const items = Array.from(e.clipboardData?.items || [])
      const imgItem = items.find(i => i.type.startsWith('image/'))
      if (imgItem) { const f = imgItem.getAsFile(); if (f) { e.preventDefault(); setNewFile(f) } }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [saving])

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true); setError(null)
    try {
      const updated = await updateDocument({ documentId: doc.id, name: title.trim() || doc.name, newFile: newFile || undefined, milestoneId: msId })
      onSaved(updated)
    } catch (err) {
      setError(err.message || 'Save failed.')
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--aj-bg-surface, #fff)', borderRadius: 10, padding: '24px 28px', minWidth: 380, maxWidth: 520, width: '100%', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 15, marginBottom: 18, color: 'var(--aj-text, #07272D)' }}>Edit Rendering</div>
        <form onSubmit={handleSave}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, opacity: 0.6, color: 'var(--aj-text, #07272D)', fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Enter a title" disabled={saving}
              style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text, #07272D)' }}
            />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, opacity: 0.6, color: 'var(--aj-text, #07272D)', fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Image</label>
            <ImageDropZone file={newFile} currentUrl={doc.url} onFile={setNewFile} disabled={saving} />
            {newFile && (
              <div style={{ marginTop: 6, fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--aj-text-muted)' }}>
                New: {newFile.name} —{' '}
                <button type="button" onClick={() => setNewFile(null)} disabled={saving}
                  style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }}
                >Cancel replacement</button>
              </div>
            )}
          </div>
          {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 12, fontFamily: 'DM Sans, sans-serif' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={saving}
              style={{ padding: '7px 18px', borderRadius: 4, border: '0.5px solid var(--aj-border, #c0d0d4)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, cursor: 'pointer', background: 'transparent', color: 'var(--aj-text, #07272D)' }}
            >Cancel</button>
            <button type="submit" disabled={saving}
              style={{ padding: '7px 18px', borderRadius: 4, border: 'none', fontFamily: 'DM Sans, sans-serif', fontSize: 13, cursor: saving ? 'default' : 'pointer', background: '#07272D', color: '#fff', opacity: saving ? 0.6 : 1 }}
            >{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ── Manual (uploaded / legacy) document row — outline row + own action menu ── */
// onEdit — optional; when provided shows "Edit title / Replace image" for non-legacy docs.
// docCtx — { msId, isPosted, projectId, projectName, msName, slackThreadTs } for the Done lock
// + Markup & QC actions (docs/DOCUMENT_MARKUP.md); onMarkDone/onUnlock omitted for legacy docs
// (they predate this app's own document model entirely).
function ManualDocRow({ doc, readOnly, onDelete, onEdit, docCtx, onMarkDone, onUnlock }) {
  const menu = useActionMenu()
  const isLink = docIsLink(doc)
  const locked = Boolean(doc.locked)
  const canEdit = Boolean(onEdit) && !doc._legacy && !locked
  const canDelete = (!readOnly && !locked) || doc._legacy
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <a className="wf-node-text" href={doc.url} target="_blank" rel="noopener noreferrer" title={doc.url}
             style={{ textDecoration: 'none', color: 'inherit', flex: '1 1 0', minWidth: 0 }}>{doc.name}</a>
          {locked && (
            <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.02em', color: '#07272D', background: '#e8c98a', borderRadius: 4, padding: '1px 6px', marginLeft: 8, flexShrink: 0 }}
              title={doc.locked_by_name ? `Marked done by ${doc.locked_by_name}` : 'Marked done'}>DONE</span>
          )}
          {doc._legacy && (
            <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--aj-text-muted)', marginLeft: 8, flexShrink: 0 }} title="Stored in Airtable (read-only)">AT</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); window.open(doc.url, '_blank', 'noopener') }}>{isLink ? 'Open link' : 'Open / download'}</ActionMenuItem>
        {!doc._legacy && (doc.file_type === 'pdf' || (doc.url || '').toLowerCase().endsWith('.pdf')) && (
          <ActionMenuItem onClick={() => { menu.close(); openDocQcTab(doc, { projectId: docCtx?.projectId, projectName: docCtx?.projectName, milestoneName: docCtx?.msName, slackThreadTs: docCtx?.slackThreadTs }) }}>Markup & QC</ActionMenuItem>
        )}
        {!doc._legacy && onMarkDone && !docCtx?.isPosted && (
          <ActionMenuItem onClick={() => { menu.close(); locked ? onUnlock(doc) : onMarkDone(doc) }}>{locked ? 'Unlock' : 'Mark done'}</ActionMenuItem>
        )}
        <ActionMenuDivider />
        {canEdit && <ActionMenuItem onClick={() => { menu.close(); onEdit(doc) }}>Edit title / Replace image</ActionMenuItem>}
        {canEdit && canDelete && <ActionMenuDivider />}
        {/* Legacy docs stay deletable even when the milestone is posted/closed-out — see handleDelete. */}
        {canDelete && <ActionMenuItem onClick={() => { menu.close(); onDelete(doc) }} danger>{doc._legacy ? 'Remove (legacy)' : 'Delete document'}</ActionMenuItem>}
      </ActionMenu>
    </div>
  )
}

/* ── Integrated (app-generated) document row — outline row + own action menu ── */
// Driven by the milestoneDocumentProviders registry; the descriptor supplies its own actions.
function IntegratedDocRow({ descriptor }) {
  const menu = useActionMenu()
  const acts = descriptor.actions || []
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ flex: '1 1 0', minWidth: 0 }}>{descriptor.title}</span>
          {descriptor.note && <span style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginLeft: 8, flexShrink: 0 }}>{descriptor.note}</span>}
        </div>
        {acts.length > 0 && <ActionMenuButton menu={menu} />}
      </div>
      {acts.length > 0 && (
        <ActionMenu menu={menu}>
          {acts.map((a, i) => (
            <ActionMenuItem key={i} danger={a.danger} onClick={() => { menu.close(); try { a.run() } catch (_) {} }}>{a.label}</ActionMenuItem>
          ))}
        </ActionMenu>
      )}
    </div>
  )
}

/* ── Sketch ribbon row (Matt, 2026-10-26) ─────────────────────────────────── */
// A Client Hub "Sketch ribbon" entry filed under this milestone (data.milestoneId). Edit opens the
// same Client Hub entry editor the Hub's own "Add entry" uses; publishing happens there.
// A "PDF document" entry (docs/CLIENT_HUB_PDF_DOCUMENTS.md) uses the same row, named for what it is.
function SketchRibbonRow({ entry, journeyId, onDelete }) {
  const menu = useActionMenu()
  const isDoc = entry.type === 'document'
  const pdf = isDoc ? (entry.data?.pdf || null) : null
  const noun = isDoc ? 'PDF document' : 'sketch ribbon'
  const n = Array.isArray(entry.data?.sketches) ? entry.data.sketches.length : 0
  const what = isDoc
    ? (pdf && pdf.url ? (pdf.pages ? pdf.pages + (pdf.pages === 1 ? ' page' : ' pages') : 'PDF') : 'No PDF yet')
    : (n ? n + (n === 1 ? ' sketch' : ' sketches') : 'No sketches yet')
  const note = what + ' · ' + (entry.published ? 'On the Client Hub' : 'Draft, not on the Hub yet')
  function edit() {
    window.dispatchEvent(new CustomEvent('open-journey-entry-modal', { detail: { journeyId, entry } }))
  }
  return (
    <div className="wf-node wf-node--item" data-key={'sketchribbon:' + entry.id}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ flex: '1 1 0', minWidth: 0, cursor: 'pointer' }} onClick={edit}>{entry.title || (isDoc ? 'PDF document' : 'Sketch ribbon')}</span>
          <span style={{ fontSize: 11, color: entry.published ? 'var(--aj-text-muted)' : '#b7791f', marginLeft: 8, flexShrink: 0 }}>{note}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); edit() }}>Edit {noun}</ActionMenuItem>
        {pdf && pdf.url && <ActionMenuItem onClick={() => { menu.close(); window.open(pdf.url, '_blank', 'noopener') }}>View PDF</ActionMenuItem>}
        <ActionMenuDivider />
        <ActionMenuItem danger onClick={() => { menu.close(); onDelete(entry) }}>Delete {noun}</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/* ── Milestone Documents section ─────────────────────────────────────────── */
// Self-contained section node (mirrors CoordDocumentsSection). Renders:
//   1. integrated documents (registry-driven: pre-design report, presentation, …) each with its own menu
//   2. manually-uploaded + legacy Airtable documents, each with its own menu
//   3. an outline-style empty state when neither exists
// Upload is an "Add document" item in the section's ⋯ menu (no inline pill).
// `sectionZoom` renders content only (the ZoomTitle supplies the header), same as coord items.
const IMAGE_FILE_TYPES = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'avif'])

function MilestoneDocumentsSection({ msId, ms, readOnly = false, sectionZoom = false }) {
  const key = sectionKey(msId, 'documents')
  const isCollapsed = wf.collapsed[key] !== false
  const docs = ctx.docCache?.[msId]
  const sectionMenu = useActionMenu()
  const [uploadOpen, setUploadOpen]           = useState(false)
  const [batchUploadOpen, setBatchUploadOpen] = useState(false)
  const [editDoc, setEditDoc]                 = useState(null)
  // Preset upload (Round: punch-list document row) — set by the 'open-milestone-doc-upload'
  // bridge event so an integrated provider row can open the SAME upload modal manual "Add
  // document" uses, pre-filled with a title and tagged with a source so it can be re-surfaced
  // as its own integrated row instead of a duplicate manual one. Null = a plain manual upload.
  const [presetUpload, setPresetUpload]       = useState(null)
  const hasLoaded = useRef(false)
  const ribbonsLoaded = useRef(false)

  const msRec     = ms || (ctx.state?.milestones || []).find(m => m.id === msId)
  const msDelType   = msRec ? (getFirst(msRec.fields?.['Name (from Deliverable)']) || '') : ''
  const msDelTypeId = msRec ? (getFirst(msRec.fields?.['Deliverable']) || '') : ''
  const projectId = getFirst(msRec?.fields?.['Project']) || ctx.recordId || wf.zoomProjectId || ''
  // Round 46 — some milestone types don't get a Documents branch at all; others keep it but can't
  // take new manual uploads (their real output already has its own dedicated place to live).
  const isDocsHidden   = DOCS_HIDDEN_MS_TYPE_IDS.includes(msDelTypeId)
  const isUploadLocked = DOCS_UPLOAD_LOCKED_MS_TYPE_IDS.includes(msDelTypeId)
  // Posted signal = the writable 'Posted Date' only (matches the engine's isPosted). Post/unpost
  // mutate this field on the in-memory record + re-render, so the lock flips immediately. Do NOT
  // also check 'Posted' — it's a computed field that stays stale locally until a reload, which kept
  // documents locked after unposting until a hard refresh.
  const isPosted  = !!(msRec?.fields?.['Posted Date'])
  // Map any frozen app-generated reports (committed at posting) to their provider key so the
  // integrated row can open the saved copy instead of regenerating. These rows are then hidden
  // from the manual list (the integrated row represents them) to avoid a duplicate.
  const frozenByKey = {}
  if (Array.isArray(docs)) {
    for (const d of docs) {
      if (d.source === 'predesign-report') frozenByKey.predesign = d
      else if (d.source === 'opc-report')  frozenByKey.opc = d
      else if (d.source === 'site-analysis') frozenByKey.siteanalysis = d
      else if (d.source === 'field-observation') frozenByKey.fieldobs = d
      else if (d.source === 'punch-list') frozenByKey.punchlist = d
      else if (d.source === 'outline-spec') frozenByKey['outline-spec'] = d
      else if (d.source === 'schedules') frozenByKey.schedules = d
    }
  }
  // Presentation review copies (api/presentation-review-copy.js) — every dated copy, newest first.
  // Surfaced on the presentation row rather than as loose document rows.
  const reviewCopies = Array.isArray(docs)
    ? docs.filter(d => d.source === 'presentation-review').sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
    : []
  // DRB presentation copies (api/drb-presentation.js) — every dated copy, newest first, on the DRB row.
  const drbCopies = Array.isArray(docs)
    ? docs.filter(d => d.source === 'drb-presentation').sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
    : []
  // ASI-type milestones mount the full ASI node (MilestoneAsiMount, below) as the first child of
  // Documents — it does its own fetch/cache of the linked ASI, including the issued PDF link on
  // its own title row. Nothing else here needs that record.
  const isAsiMs = isAsiMilestoneType(msDelTypeId)
  // RFI / Submittal records sit under Documents the same way (Matt, 2026-09-24: "The RFI should show
  // under the Document node, expandable as usual") — they ARE the milestone's output, like an ASI.
  const isRfiDocMs = isRfiMilestoneType(msDelTypeId)
  const isSubmittalDocMs = isSubmittalMilestoneType(msDelTypeId)

  // Punch-list document row (Substantial Completion) — opens the standard manual-upload modal
  // pre-filled/tagged via the bridge event below, rather than a dedicated upload flow.
  useEffect(() => {
    function onOpenUpload(e) {
      if (!e.detail || e.detail.msId !== msId) return
      setPresetUpload({ title: e.detail.presetName || '', source: e.detail.presetSource || undefined })
      setUploadOpen(true)
    }
    window.addEventListener('open-milestone-doc-upload', onOpenUpload)
    return () => window.removeEventListener('open-milestone-doc-upload', onOpenUpload)
  }, [msId])

  // Mark done / Unlock (docs/DOCUMENT_MARKUP.md) — the app-generated providers
  // (milestoneDocumentProviders.js) can't call the service layer directly (this file's own
  // "never call an API directly" rule for that module), so they dispatch these events for this
  // host component to handle, same pattern as open-milestone-doc-upload above.
  function patchDocInCache(documentId, patch) {
    if (!ctx.docCache || !Array.isArray(ctx.docCache[msId])) return
    ctx.docCache[msId] = ctx.docCache[msId].map(d => d.id === documentId ? { ...d, ...patch } : d)
    window.dispatchEvent(new Event('outline-render'))
  }
  async function handleMarkDone(doc) {
    const actorName = ctx.currentMember?.fields?.['Member Name'] || ctx.currentMember?.fields?.['Name'] || ctx.currentMember?.fields?.['Full Name'] || ''
    patchDocInCache(doc.id, { locked: true, locked_by_name: actorName })
    try { await markDocumentDone(doc.id, actorName) } catch (ex) { console.error('[Documents] markDone failed:', ex.message); patchDocInCache(doc.id, { locked: false, locked_by_name: null }) }
  }
  async function handleUnlock(doc) {
    patchDocInCache(doc.id, { locked: false, locked_by_name: null })
    try { await unlockDocument(doc.id) } catch (ex) { console.error('[Documents] unlock failed:', ex.message); patchDocInCache(doc.id, { locked: true }) }
  }
  useEffect(() => {
    function onMarkDone(e) { if (e.detail?.msId === msId) { const d = (ctx.docCache?.[msId] || []).find(x => x.id === e.detail.documentId); if (d) handleMarkDone(d) } }
    function onUnlock(e) { if (e.detail?.msId === msId) { const d = (ctx.docCache?.[msId] || []).find(x => x.id === e.detail.documentId); if (d) handleUnlock(d) } }
    window.addEventListener('mark-document-done', onMarkDone)
    window.addEventListener('unlock-document', onUnlock)
    return () => { window.removeEventListener('mark-document-done', onMarkDone); window.removeEventListener('unlock-document', onUnlock) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msId])

  // Selections Package rows (docs/SELECTIONS_PACKAGE_DOCUMENTS.md) — "Mark complete" renders the
  // review copy server-side, saves it as the row's one document, and locks it. The row (source =
  // kind) replaces any earlier copy in the cache.
  useEffect(() => {
    async function onComplete(e) {
      if (e.detail?.msId !== msId) return
      const kind = e.detail.kind
      const label = SELECTIONS_DOC_LABELS[kind] || 'Document'
      const actorName = ctx.currentMember?.fields?.['Member Name'] || ctx.currentMember?.fields?.['Name'] || ctx.currentMember?.fields?.['Full Name'] || ''
      showPill('Saving ' + label + '…')
      try {
        const doc = await completeSelectionsDoc({ milestoneId: msId, projectId, projectName, kind, actorName })
        if (!ctx.docCache) ctx.docCache = {}
        ctx.docCache[msId] = [...(ctx.docCache[msId] || []).filter(d => d.source !== kind), doc]
        showPill(label + ' marked complete')
        window.dispatchEvent(new Event('outline-render'))
      } catch (ex) {
        showPill('Not saved', true)
        const { alertDialog, confirmDialog } = await import('./Dialog.jsx')
        if (ex.code === 'empty') {
          // Nothing sent to this milestone yet: offer the one screen that fixes it (AlderSPEC opens
          // on Releases with this milestone expanded when given msId and no tab).
          const go = await confirmDialog({ title: 'Nothing in this package yet', message: ex.message, confirmLabel: 'Open AlderSPEC Releases', cancelLabel: 'Close' })
          if (go) window.open('/spec?projectId=' + encodeURIComponent(projectId || '') + '&msId=' + encodeURIComponent(msId)
            + (projectName ? '&projectName=' + encodeURIComponent(projectName) : ''), '_blank')
          return
        }
        await alertDialog({ title: 'Could not mark complete', message: ex.message || String(ex) })
      }
    }
    window.addEventListener('selections-doc-complete', onComplete)
    return () => window.removeEventListener('selections-doc-complete', onComplete)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msId, projectId])

  const msDisplayName = msDelType || getFirst(msRec?.fields?.['Full Deliverable Name']) || ''
  const projectName   = getFirst(msRec?.fields?.['Project Name (from Project)']) || getFirst(msRec?.fields?.['Project Name']) || ctx.projectName || ''
  const integrated = getIntegratedDocs({
    msId, msDelType, msDelTypeId, projectId, isPosted, frozenByKey, reviewCopies, drbCopies, docsLoading: docs === undefined || docs === null,
    msName: msDisplayName, projectName,
  })
  // Context the Done lock + Markup & QC actions need but a plain document row doesn't itself
  // carry (docs/DOCUMENT_MARKUP.md).
  const docCtx = {
    msId, isPosted, projectId, projectName, msName: msDisplayName,
    slackThreadTs: getFirst(msRec?.fields?.['Slack Thread Timestamp']) || '',
  }

  // Review copy of the design presentation (docs/DOCUMENT_MARKUP.md "Presentation review
  // copies") — fired by the presentation row's action; handled here so the new document lands in
  // this section's cache and the copy opens straight into AlderQC.
  useEffect(() => {
    function onMakeReviewCopy(e) {
      if (e.detail?.msId !== msId) return
      makePresentationReviewCopy({ msId, projectId, projectName, msName: msDisplayName, slackThreadTs: docCtx.slackThreadTs })
        .then(res => {
          if (res.error) { import('./Dialog.jsx').then(m => m.alertDialog({ title: 'Could not make a review copy', message: res.error })); return }
          if (res.document && ctx.docCache && Array.isArray(ctx.docCache[msId])) {
            ctx.docCache[msId] = [...ctx.docCache[msId], res.document]
            window.dispatchEvent(new Event('outline-render'))
          }
        })
    }
    window.addEventListener('make-presentation-review-copy', onMakeReviewCopy)
    return () => window.removeEventListener('make-presentation-review-copy', onMakeReviewCopy)
  })

  // The DRB row's action opens DrbPresentationHost (mounted in OutlineView) — the DAPC milestones hide
  // Documents, so the window can't live here.

  // Manual uploads are editable only while the milestone is open. Once posted (or the phase is
  // closed-out → readOnly), uploading and deleting are disabled until the milestone is unposted.
  const docsLocked = readOnly || isPosted

  function maybeLoad() {
    if (hasLoaded.current) return
    if (!ctx.docCache) ctx.docCache = {}
    if (ctx.docCache[msId] !== undefined) { hasLoaded.current = true; return }
    hasLoaded.current = true
    ctx.docCache[msId] = null
    window.dispatchEvent(new Event('outline-render'))
    getMilestoneDocuments(msId)
      .then(res => { if (ctx.docCache) ctx.docCache[msId] = res.documents || []; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { if (ctx.docCache) ctx.docCache[msId] = []; window.dispatchEvent(new Event('outline-render')) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isDocsHidden && (!isCollapsed || sectionZoom)) maybeLoad() }, [isCollapsed, sectionZoom, isDocsHidden])

  // Client Hub entries filed under this milestone — sketch ribbons and PDF documents
  // (ctx.sketchRibbonCache[msId]: undefined = not started, null = in flight, { journeyId, entries }
  // = loaded). Reloaded whenever the Hub changes.
  function loadRibbons(force) {
    if (!projectId) return
    if (!ctx.sketchRibbonCache) ctx.sketchRibbonCache = {}
    if (!force && ctx.sketchRibbonCache[msId] !== undefined) return
    if (!force) ctx.sketchRibbonCache[msId] = null
    getHubEntriesForMilestone(projectId, msId)
      .then(r => { ctx.sketchRibbonCache[msId] = r; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.sketchRibbonCache[msId] = { journeyId: null, entries: [] }; window.dispatchEvent(new Event('outline-render')) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isDocsHidden || (isCollapsed && !sectionZoom) || ribbonsLoaded.current) return
    ribbonsLoaded.current = true
    loadRibbons(false)
  }, [isCollapsed, sectionZoom, isDocsHidden])
  useEffect(() => {
    function onHub() {
      if (!ctx.sketchRibbonCache || ctx.sketchRibbonCache[msId] === undefined) return
      loadRibbons(true)
      // Publishing a ribbon can tick this milestone's checklist on the server — reread it.
      if (ctx.clCache && ctx.clCache[msId] !== undefined) {
        getMilestoneChecklistItems(msId).then(items => { ctx.clCache[msId] = items; window.dispatchEvent(new Event('outline-render')) }).catch(() => {})
      }
    }
    window.addEventListener('journey-updated', onHub)
    return () => window.removeEventListener('journey-updated', onHub)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msId, projectId])

  // Branch removed entirely for these milestone types (Round 46) — after all hooks, so this stays
  // a valid early return regardless of which type renders. sectionZoom mode still has to return
  // SOME content (a deep-linked/back-button visit to the zoom route shouldn't render blank).
  if (isDocsHidden) {
    return sectionZoom
      ? <div className="wf-list"><div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>Documents aren't used for this milestone type.</div></div>
      : null
  }

  const ribbonState = ctx.sketchRibbonCache ? ctx.sketchRibbonCache[msId] : undefined
  const ribbons = ribbonState && Array.isArray(ribbonState.entries) ? ribbonState.entries : []
  const sketchRibbons = ribbons.filter(e => e.type === 'sketches')
  const hubDocs = ribbons.filter(e => e.type === 'document')

  // A PDF sent whole to the Client Hub, filed here (docs/CLIENT_HUB_PDF_DOCUMENTS.md). Offered on
  // every milestone that takes sketch ribbons, including the ones closed to manual uploads.
  function addHubDocument() {
    sectionMenu.close()
    const today = new Date()
    const ymd = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0')
    window.dispatchEvent(new CustomEvent('open-journey-entry-modal', { detail: {
      journeyId: ribbonState?.journeyId || null,
      projectId, projectName: getFirst(msRec?.fields?.['Project Name (from Project)']) || '',
      presetType: 'document',
      presetTitle: '',
      presetPhase: getFirst(msRec?.fields?.['Project Phase']) || '',
      presetData: { milestoneId: msId, entryDate: ymd },
    } }))
  }

  function addSketchRibbon() {
    sectionMenu.close()
    const today = new Date()
    const ymd = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0')
    window.dispatchEvent(new CustomEvent('open-journey-entry-modal', { detail: {
      journeyId: ribbonState?.journeyId || null,
      projectId, projectName: getFirst(msRec?.fields?.['Project Name (from Project)']) || '',
      presetType: 'sketches',
      presetTitle: (msDelType || 'Sketches') + (sketchRibbons.length ? ' · ' + (sketchRibbons.length + 1) : ''),
      presetPhase: getFirst(msRec?.fields?.['Project Phase']) || '',
      presetData: { milestoneId: msId, entryDate: ymd },
    } }))
  }

  async function handleDeleteRibbon(entry) {
    const { confirmDialog } = await import('./Dialog.jsx')
    const ok = await confirmDialog({
      title: entry.type === 'document' ? 'Delete PDF document?' : 'Delete sketch ribbon?',
      message: 'Delete "' + (entry.title || (entry.type === 'document' ? 'PDF document' : 'Sketch ribbon')) + '"? It comes off the Client Hub too.',
      confirmLabel: 'Delete', danger: true,
    })
    if (!ok) return
    if (ribbonState) ctx.sketchRibbonCache[msId] = { ...ribbonState, entries: ribbons.filter(e => e.id !== entry.id) }
    window.dispatchEvent(new Event('outline-render'))
    try { await deleteJourneyEntry(entry.id) } catch (ex) { console.error('[Documents] ribbon delete failed:', ex.message); loadRibbons(true) }
  }

  function handleUploaded(doc) {
    setUploadOpen(false)
    if (!ctx.docCache) ctx.docCache = {}
    ctx.docCache[msId] = [...(ctx.docCache[msId] || []), doc]
    window.dispatchEvent(new Event('outline-render'))
  }

  function handleBatchUploaded(newDocs) {
    setBatchUploadOpen(false)
    if (!ctx.docCache) ctx.docCache = {}
    ctx.docCache[msId] = [...(ctx.docCache[msId] || []), ...newDocs]
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleDownloadRenderingPdf() {
    sectionMenu.close()
    const allDocs = Array.isArray(docs) ? docs : []
    const imageDocs = allDocs.filter(d => IMAGE_FILE_TYPES.has((d.file_type || '').toLowerCase()))
    if (imageDocs.length === 0) {
      const { alertDialog: alert } = await import('./Dialog.jsx')
      await alert({ title: 'No images', message: 'Upload at least one image to this milestone before generating the PDF.' })
      return
    }
    // Open the tab synchronously so the browser does not block it as a popup,
    // then point it at the PDF once the blob is ready — matching the pre-design report pattern.
    const tab = window.open('', '_blank')
    if (tab) {
      tab.document.write('<body style="margin:0;font-family:\'DM Sans\',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#f4f7f8;color:#07272D;"><p style="font-size:13px;color:#9ab0ba;letter-spacing:0.01em">Generating PDF package…</p></body>')
      tab.document.close()
    }
    const token = await window.__clerkGetToken?.()
    try {
      const msRec_ = ms || (ctx.state?.milestones || []).find(m => m.id === msId)
      const msTitle  = getFirst(msRec_?.fields?.['Full Deliverable Name']) || getFirst(msRec_?.fields?.['Name']) || 'Rendering Package'
      const projName = msRec_?.fields?.['Project Name'] || ''
      const res = await fetch('/api/render-package-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ imageUrls: imageDocs.map(d => d.url), milestoneTitle: msTitle, projectName: projName }),
      })
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'PDF failed') }
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      if (tab) tab.location = url
      else window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 30000)
    } catch (err) {
      if (tab) tab.close()
      const { alertDialog: alert } = await import('./Dialog.jsx')
      await alert({ title: 'PDF error', message: err.message || 'Could not generate PDF.' })
    }
  }
  async function handleDelete(doc) {
    // Posted / closed-out locks deletion of app-managed (Supabase) documents — that's the actual
    // deliverable content. Legacy Airtable documents predate this app's posting workflow entirely
    // (they were never part of what a post locks), so removing a stray/mistaken one — e.g. a rolled-
    // up drawing-set PDF that shouldn't have been attached — stays available regardless of post state.
    if ((docsLocked || doc.locked) && !doc._legacy) return
    // Legacy docs delete the Airtable record (more consequential) — confirm first.
    if (doc._legacy) {
      const { confirmDialog } = await import('./Dialog.jsx')
      const ok = await confirmDialog({
        title: 'Remove legacy document?',
        message: `Remove "${doc.name}"? This deletes the record from the old app's Documents table and cannot be undone.`,
        confirmLabel: 'Remove',
        danger: true,
      })
      if (!ok) return
    }
    // Optimistic remove; list re-syncs on next load if the server call fails.
    if (ctx.docCache) ctx.docCache[msId] = (ctx.docCache[msId] || []).filter(d => d.id !== doc.id)
    window.dispatchEvent(new Event('outline-render'))
    try { await (doc._legacy ? deleteLegacyDocument(doc.id) : deleteDocument(doc.id)) }
    catch (ex) { console.error('[Documents] delete failed:', ex.message) }
  }

  function handleDocEdited(updatedDoc) {
    setEditDoc(null)
    if (ctx.docCache && Array.isArray(ctx.docCache[msId])) {
      ctx.docCache[msId] = ctx.docCache[msId].map(d => d.id === updatedDoc.id ? { ...d, ...updatedDoc } : d)
    }
    window.dispatchEvent(new Event('outline-render'))
  }

  const manualDocs    = Array.isArray(docs) ? docs : null
  // Frozen app-generated reports/tools are surfaced by their integrated row instead (which opens
  // the committed copy, or — for Site Analysis — offers a Download PDF action alongside Open), so
  // hide their raw rows here to avoid showing the same document twice at two different lines.
  const displayDocs   = manualDocs ? manualDocs.filter(d =>
    d.source !== 'predesign-report' && d.source !== 'opc-report' && d.source !== 'site-analysis'
    && d.source !== 'field-observation' && d.source !== 'punch-list' && d.source !== 'presentation-review'
    && d.source !== 'drb-presentation'
    && d.source !== 'outline-spec' && d.source !== 'schedules'
  ) : null
  const loading       = docs === undefined || docs === null
  const totalCount    = (displayDocs ? displayDocs.length : 0) + integrated.length + ribbons.length
  const isRenderingPkg = msDelTypeId === RENDERING_PACKAGE_TYPE_ID

  // In RP milestones, image doc rows get an "Edit" handler so users can rename / replace the image.
  // Editing is allowed even when the milestone is posted (it's cosmetic, not a deliverable change).
  function rpEditHandler(doc) {
    if (!IMAGE_FILE_TYPES.has((doc.file_type || '').toLowerCase())) return undefined
    return setEditDoc
  }

  const contentRows = (
    <>
      {/* ASI — the canonical AsiNode from Reference > Construction, mounted under Documents
          (Matt, 2026-09-18 — previously sat at the top of the milestone's whole section list,
          moved here since the ASI record IS this milestone's actual output). Renders null if
          this milestone has no linked ASI yet (e.g. was created manually via AddMilestoneWizard). */}
      {isAsiMs && <MilestoneAsiMount ms={msRec} projectId={projectId} />}
      {isRfiDocMs && <MilestoneRfiMount ms={msRec} projectId={projectId} />}
      {isSubmittalDocMs && <MilestoneSubmittalMount ms={msRec} projectId={projectId} />}
      {sketchRibbons.map(e => <SketchRibbonRow key={'rib:' + e.id} entry={e} journeyId={ribbonState.journeyId} onDelete={handleDeleteRibbon} />)}
      {hubDocs.map(e => <SketchRibbonRow key={'hdoc:' + e.id} entry={e} journeyId={ribbonState.journeyId} onDelete={handleDeleteRibbon} />)}
      {integrated.map(d => <IntegratedDocRow key={'int:' + d.key} descriptor={d} />)}
      {loading && (
        <div className="wf-node wf-node--item">
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
            <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>
          </div>
        </div>
      )}
      {!loading && displayDocs && displayDocs.map(doc => (
        <ManualDocRow key={doc.id} doc={doc} readOnly={docsLocked} onDelete={handleDelete}
          onEdit={isRenderingPkg ? rpEditHandler(doc) : undefined}
          docCtx={docCtx} onMarkDone={handleMarkDone} onUnlock={handleUnlock} />
      ))}
      {!loading && totalCount === 0 && (
        <div className="wf-node wf-node--item">
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
            <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No documents yet.</span></div>
          </div>
        </div>
      )}
    </>
  )

  // Section-zoom mode: content only (ZoomTitle supplies the header). Add-document is reached
  // from the section's ⋯ menu in the non-zoom views, matching the coordination-item pattern.
  if (sectionZoom) {
    return (
      <>
        <div className="wf-list">{contentRows}</div>
        {uploadOpen && <MilestoneDocUploadModal msId={msId} initialTitle={presetUpload?.title} source={presetUpload?.source} onClose={() => { setUploadOpen(false); setPresetUpload(null) }} onDone={doc => { handleUploaded(doc); setPresetUpload(null) }} />}
        {batchUploadOpen && <BatchRenderingUploadModal msId={msId} onClose={() => setBatchUploadOpen(false)} onDone={handleBatchUploaded} />}
        {editDoc && <EditRenderingDocModal doc={editDoc} msId={msId} onClose={() => setEditDoc(null)} onSaved={handleDocEdited} />}
      </>
    )
  }

  const count = (loading && integrated.length === 0) ? null : totalCount
  // Round 46 — Pre-design Research, Conceptual Framework, and Integrated Design Proposal keep the
  // branch but can't take new manual uploads (see isUploadLocked above). "Add document" drops out
  // of the menu for them, so the trigger itself must not render an empty menu.
  // Site Analysis is reachable ONLY via its dedicated row on Pre-design Research (see
  // milestoneDocumentProviders.js) — a generic "Site analysis…" entry used to also appear in this
  // menu on every other milestone type, but that's confusing tool access outside the one place the
  // tool actually belongs, so it was removed (2026-07).
  const showAddDoc         = !isUploadLocked
  // Sketch ribbons (Matt, 2026-10-26) go on the client's Hub, not into a locked deliverable, so
  // they can be added even after posting (readOnly includes posted). A milestone still waiting on
  // its predecessor stays closed to work, ribbons included.
  const canAddRibbon       = !!projectId && (!readOnly || isPosted)
  const hasMenuItems       = isRenderingPkg || showAddDoc
  return (
    <div className={`wf-node wf-node--section wf-node--section-documents${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" data-fn="zoomTo" data-level="section" data-sectionkey={key} title="Focus on Documents"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Documents</span>
          {count != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{count}</span>}
        </div>
        {((!docsLocked && hasMenuItems) || canAddRibbon) && <ActionMenuButton menu={sectionMenu} />}
      </div>
      {((!docsLocked && hasMenuItems) || canAddRibbon) && (
        <ActionMenu menu={sectionMenu}>
          {canAddRibbon && <ActionMenuItem onClick={addSketchRibbon}>Add sketch ribbon</ActionMenuItem>}
          {canAddRibbon && <ActionMenuItem onClick={addHubDocument}>Add PDF document</ActionMenuItem>}
          {canAddRibbon && !docsLocked && hasMenuItems && <ActionMenuDivider />}
          {docsLocked || !hasMenuItems ? null : isRenderingPkg ? (
            <>
              <ActionMenuItem onClick={() => { sectionMenu.close(); setBatchUploadOpen(true) }}>Add renderings</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem onClick={handleDownloadRenderingPdf}>Download PDF package</ActionMenuItem>
            </>
          ) : (
            <>
              {showAddDoc && <ActionMenuItem onClick={() => { sectionMenu.close(); setUploadOpen(true) }}>Add document</ActionMenuItem>}
            </>
          )}
        </ActionMenu>
      )}
      {!isCollapsed && <div className="wf-children">{contentRows}</div>}
      {uploadOpen && <MilestoneDocUploadModal msId={msId} initialTitle={presetUpload?.title} source={presetUpload?.source} onClose={() => { setUploadOpen(false); setPresetUpload(null) }} onDone={doc => { handleUploaded(doc); setPresetUpload(null) }} />}
      {batchUploadOpen && <BatchRenderingUploadModal msId={msId} onClose={() => setBatchUploadOpen(false)} onDone={handleBatchUploaded} />}
      {editDoc && <EditRenderingDocModal doc={editDoc} msId={msId} onClose={() => setEditDoc(null)} onSaved={handleDocEdited} />}
    </div>
  )
}

/* ── Criteria section — mount of the Design "Criteria" branch under a milestone ── */
// Owner Criteria are project-scoped (getCriteria(projectId)); ctx.criteriaCache mirrors the engine's
// project cache: null = not started / in-flight, [] / [...] = loaded. Mounted under Pre-design
// Research milestones — shows the client's submitted answers grouped by questionnaire category via
// CriteriaCategoryRows (DesignSection.jsx) — the same component Reference > Design > Criteria uses,
// so both places expand/collapse identically (CLAUDE.md's One-Component-Per-Node-Type rule).
function CriteriaSection({ msId, readOnly = false, sectionZoom = false }) {
  const criteria  = ctx.criteriaCache
  const projectId = ctx.recordId || wf.zoomProjectId || ''
  const key = sectionKey(msId, 'criteria')
  const isCollapsed = wf.collapsed[key] !== false
  const hasLoaded = useRef(false)

  function maybeLoad() {
    if (hasLoaded.current) return
    if (ctx.criteriaCache !== null) { hasLoaded.current = true; return }  // loaded or in-flight elsewhere
    if (!projectId) return
    hasLoaded.current = true
    ctx.criteriaCache = []  // in-flight marker (cross-instance guard, matches engine loadCriteria)
    getCriteria(projectId)
      .then(records => { ctx.criteriaCache = records; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.criteriaCache = []; window.dispatchEvent(new Event('outline-render')) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isCollapsed || sectionZoom) maybeLoad() }, [isCollapsed, sectionZoom])

  const rows   = Array.isArray(criteria) ? criteria : null
  const record = rows && rows.length ? rows[0] : null      // one Owner Criteria record per project

  function emptyRow(text, italic) {
    return (
      <div className="wf-node wf-node--item">
        <div className="wf-row">
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
          <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
          <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: italic ? 'italic' : 'normal' }}>{text}</span></div>
        </div>
      </div>
    )
  }

  const contentRows = (
    <>
      {criteria === null && emptyRow('Loading…')}
      {rows && !record && emptyRow('Criteria not collected yet — gathered when the client completes the onboarding questionnaire.', true)}
      <CriteriaCategoryRows record={record} ownerKey={msId} />
    </>
  )

  // Section-zoom mode: content only (ZoomTitle supplies the header).
  if (sectionZoom) return <div className="wf-list">{contentRows}</div>

  return (
    <SectionNode msId={msId} type="criteria" label="Criteria" count={criteriaAnsweredTotal(record)}>
      {contentRows}
    </SectionNode>
  )
}

/* ── Parcels section — mount of the Design "Parcels" branch under a milestone ── */
// Shares ctx.parcelsCache with DesignSection; null = not started / in-flight, [] / [...] = loaded.
// Mounted under Pre-design Research so parcel data is accessible without navigating to Design.
function ParcelsSection({ msId, readOnly = false, sectionZoom = false }) {
  const parcels   = ctx.parcelsCache
  const projectId = ctx.recordId || wf.zoomProjectId || ''
  const key = sectionKey(msId, 'parcels')
  const isCollapsed = wf.collapsed[key] !== false
  const hasLoaded = useRef(false)

  function maybeLoad() {
    if (hasLoaded.current) return
    if (ctx.parcelsCache !== null) { hasLoaded.current = true; return }
    if (!projectId) return
    hasLoaded.current = true
    ctx.parcelsCache = []
    getParcels(projectId)
      .then(records => { ctx.parcelsCache = records; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.parcelsCache = []; window.dispatchEvent(new Event('outline-render')) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isCollapsed || sectionZoom) maybeLoad() }, [isCollapsed, sectionZoom])

  const rows = Array.isArray(parcels) ? parcels : null
  const contentRows = (
    <>
      {parcels === null && (
        <div className="wf-node wf-node--item">
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
            <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>
          </div>
        </div>
      )}
      {rows && rows.length === 0 && (
        <div className="wf-node wf-node--item">
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
            <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No parcels defined.</span></div>
          </div>
        </div>
      )}
      {rows && rows.map(rec => <ParcelRow key={rec.id} rec={rec} projectId={projectId} />)}
    </>
  )

  if (sectionZoom) return <div className="wf-list">{contentRows}</div>

  return (
    <SectionNode msId={msId} type="parcels" label="Parcels" count={rows ? rows.length : null}>
      {contentRows}
    </SectionNode>
  )
}

/* ── Checklist item node — mirrors buildItemNode ─────────────────────────── */
export function ItemNode({ item, msId, readOnly = false }) {
  const f = item.fields
  const itemId = item.record.id
  const rawItemName = getFirst(f['Name for List (from Deliverable Checklist Item Template)']) || 'Item'
  // Strip the "(more...✚)" suffix — the expand triangle below replaces this dead visual
  // indicator (same fix already shipped for sheet checklist items — see SheetClItemNode).
  const itemName = rawItemName.replace(' (more...✚)', '').trim()
  const isActuallyDone = f['Complete'] === true
  const isDone = item.isComplete
  const isSkip = f['Milestone Not Required'] === true
  const key = itemKey(itemId)

  // Missing-context child node — the item's Summary/Instructions + learning resource link,
  // previously only visible by zooming into the item (ItemDetailPanel). Reuses `key` as the
  // expand key (same convention as SheetClItemNode) so Space/keyboard nav toggles it for free.
  const itemSummary = getFirst(f['Summary (from Deliverable Checklist Item Template)']) || ''
  const previewUrl   = getFirst(f['Preview URL (from Learning Unit Module) (from Deliverable Checklist Item Template)']) || ''
  const hasContext   = !!(itemSummary || previewUrl)
  const isExpanded    = hasContext && wf.collapsed[key] === false
  function toggleExpand(e) {
    e.stopPropagation()
    wf.collapsed[key] = isExpanded ? true : false
    window.dispatchEvent(new Event('outline-render'))
  }

  const cat = getFirst(f['Category (from Deliverable Checklist Item Template)']) || ''
  const phId = wf.zoomPhaseId || ''
  const hideDone = wf.hideDoneKeys || {}
  const shouldHide = (isDone || isSkip) && !!(
    hideDone['global'] || (phId && hideDone['ph:' + phId]) ||
    hideDone['ms:' + msId] || hideDone['cat:' + msId + ':' + cat]
  )

  const inSelect = wf.selectMode === msId + ':' + cat || wf.selectMode === msId + ':ALL'
  const isSel = (wf.selected || []).indexOf(itemId) !== -1

  // Skip badge
  let skipBadge = null
  if (isSkip) {
    const reason = f['Milestone Not Required Reason'] || ''
    const mIds = f['Milestone Not Required Member'] || []
    const member = mIds.length ? getMemberName(mIds[0]) : ''
    const tip = [reason, member ? 'by ' + member : ''].filter(Boolean).join(' — ') || 'Not required'
    skipBadge = (
      <span className="wf-badge wf-badge--amber" title={tip} style={{ cursor: 'help' }}>
        Not required{member ? ' · ' + member : ''}
      </span>
    )
  }

  // Done badge
  let doneBadge = null
  if (isDone) {
    const compMsg = (f['Completed Message'] || '').replace(/__/g, '').trim()
    const compIds = f['Completed by'] || []
    const compName = compIds.length ? getMemberName(compIds[0]) : ''
    const compDate = (f['Completed Date'] || '').slice(0, 10)
    const tip = compMsg || (compName ? compName + (compDate ? ' · ' + compDate : '') : 'Done')
    doneBadge = (
      <span className="wf-badge wf-badge--green" title={tip}>
        {compName || 'Done'}{compDate ? ' · ' + compDate : ''}
      </span>
    )
  }

  const panel = wf.panels && wf.panels[key]

  return (
    <div
      className={[
        'wf-node wf-node--item',
        isDone || isSkip ? 'wf-node--done' : '',
        shouldHide ? 'wf-hidden-done' : '',
      ].filter(Boolean).join(' ')}
      data-key={key}
    >
      <div className="wf-row">
        {hasContext ? (
          <button className="wf-triangle" onClick={toggleExpand} title={isExpanded ? 'Hide context' : 'Show context'}>
            <span className={`wf-tri-icon ${isExpanded ? 'wf-tri-icon--down' : 'wf-tri-icon--right'}`}></span>
          </button>
        ) : (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        )}
        {!readOnly && (
          <button
            className={hasContext ? 'wf-menu-btn' : 'wf-menu-btn wf-menu-btn--triangle'}
            data-fn="openMenu"
            data-key={key}
            data-type="item"
            data-itemid={itemId}
            data-msid={msId}
            data-isdone={isActuallyDone ? 'true' : 'false'}
            data-isskip={isSkip ? 'true' : 'false'}
          >&#8943;</button>
        )}
        {readOnly && <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>}
        {inSelect && !isDone && !isSkip ? (
          <input
            type="checkbox"
            className="wf-item-cb"
            checked={isSel}
            onChange={e => {
              const idx = (wf.selected || []).indexOf(itemId)
              if (e.target.checked && idx === -1) {
                if (!wf.selected) wf.selected = []
                wf.selected.push(itemId)
              } else if (!e.target.checked && idx !== -1) {
                wf.selected.splice(idx, 1)
              }
              window.dispatchEvent(new CustomEvent('outline-render'))
            }}
            data-fn="selectItem"
            data-itemid={itemId}
            data-msid={msId}
          />
        ) : (
          <button
            className={`wf-bullet wf-bullet--item${isDone ? ' wf-bullet--done' : ''}`}
            data-fn="zoomTo"
            data-level="item"
            data-itemid={itemId}
            data-msid={msId}
            data-pid={wf.zoomPhaseId || ''}
            data-cat={wf.zoomCat || ''}
            title="Focus on this item"
          ></button>
        )}
        <div className="wf-text-wrap">
          <span className={`wf-node-text wf-node-text--item${isDone || isSkip ? ' wf-node-text--done' : ''}`}>
            {itemName}
          </span>
          <span className="wf-meta">
            {skipBadge}
            {doneBadge}
          </span>
        </div>
      </div>
      {isExpanded && (
        <div className="wf-children">
          {itemSummary && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" />
                <div className="wf-text-wrap">
                  <span className="wf-node-text wf-node-text--item" style={{ color: 'var(--aj-text-mid)', fontWeight: 400, whiteSpace: 'normal' }}>
                    {itemSummary}
                  </span>
                </div>
              </div>
            </div>
          )}
          {previewUrl && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" />
                <div className="wf-text-wrap">
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1a6fa8' }}>
                    View learning resource ↗
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {panel && <div dangerouslySetInnerHTML={{ __html: panel }} />}
    </div>
  )
}

/* ── Section content — shared by inline (MilestoneRow) and zoom views ────── */
function MsContent({ msId, ms: propMs, readOnly = false }) {
  // propMs is passed from the parent row when ctx.state.milestones may not contain this record
  // (e.g. at home level showing multiple projects where only one project is the "active" ctx project)
  const ms = propMs || (ctx.state?.milestones || []).find(m => m.id === msId)
  if (!ms) return null

  // A posted milestone locks all downstream content — fold into readOnly so every child respects it.
  readOnly = readOnly || !!(ms.fields['Posted Date'])
  // So does one waiting on a predecessor (outline/blocked.js) — EXCEPT the kit: kit items are facts
  // about the project, and gathering them before the work starts is the point of the full kit.
  const kitReadOnly = readOnly
  readOnly = readOnly || isBlocked(ms)

  const msDelType   = getFirst(ms.fields['Name (from Deliverable)']) || ''
  const msDelTypeId = getFirst(ms.fields['Deliverable']) || ''
  const projectId = getFirst(ms.fields['Project']) || ctx.recordId || wf.zoomProjectId || ''
  // Mounts the Concepts branch (Reference > Design > Concepts, reused) under ICP, Conceptual
  // Framework, Integrated Design Proposal, and BIM Coordination milestones — ICP owns the full
  // Units/Levels/Rooms hierarchy underneath; the others show the list for reference and
  // selection only.
  const isConceptBranchMs = CONCEPT_BRANCH_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
  const isDapcDrawingsMs = msDelTypeId === DAPC_DRAWINGS_MS_TYPE_ID
  const isBimMs = BIM_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
  // The FAR/area breakdown (Area Plan Metrics) isn't needed until DAPC Drawings — hide it on the
  // other concept-branch milestones (CF/IDP/BIM Coordination). ICP still shows it (owns the full
  // hierarchy).
  const hideAreaMetrics = msDelTypeId === CONCEPTUAL_FRAMEWORK_TYPE_ID || msDelTypeId === INTEGRATED_DESIGN_PROPOSAL_TYPE_ID || isBimMs
  // Only this milestone's own coordination phase is relevant here — Reference > Design > BIM
  // Systems (no msDelTypeId in scope) still shows every phase a system has criteria for.
  const bimRestrictPhase = BIM_MS_TYPE_PHASE[msDelTypeId] || null
  const is3DMModel = msDelTypeId === EXISTING_BUILDING_3D_MODEL_TYPE_ID
  const isPermitMs = PERMIT_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
  const isBidMs = msDelTypeId === BID_CAMPAIGN_MS_TYPE_ID || msDelTypeId === BID_ADDENDUM_MS_TYPE_ID
  const isVeMs       = msDelTypeId === VALUE_ENGINEERING_MS_TYPE_ID
  const isSpecMs     = Object.prototype.hasOwnProperty.call(SPEC_RELEASE_MS_TYPE_IDS, msDelTypeId)
  // Selections milestones carry their Outline Spec / Schedules as Documents rows instead of the
  // standalone Specifications / Schedules branches (docs/SELECTIONS_PACKAGE_DOCUMENTS.md).
  const isSelectionsDocsMs = selectionsDocKinds(msDelTypeId).length > 0
  const isNoSheetsMs = isNoSheetsMsType(msDelTypeId)
  const isRfiMs      = isRfiMilestoneType(msDelTypeId)
  const isSubmittalMs = isSubmittalMilestoneType(msDelTypeId)
  // POST_REVISION_TYPE_IDS resolve milestonePhasePrefix to null (see resolveMilestonePhasePrefix —
  // the phase-prefix checklist filter is intentionally disabled for revision rounds), but that
  // shouldn't also hide the Drawing Sheets section itself: a brand-new revision milestone has
  // zero sheets yet and still needs the section (and its ⋯ menu's Add sheets action) to pull
  // sheets into it in the first place.
  // ASIs (Matt, 2026-10-21) revise sheets during construction the same way, from the Construction
  // Support phase, which has no drawing-release prefix — so they ride this same flag.
  const isRevisionMs = POST_REVISION_TYPE_IDS.includes(msDelTypeId) || isAsiMilestoneType(msDelTypeId)

  // Resolve the phase prefix for drawing-sheet checklists.
  // Primary: look up by Project Phase field. Fallback: look up by deliverable type id
  // so a second instance of the same milestone type works even if its Project Phase
  // field is blank or non-standard.
  const phaseName = ms.fields?.['Project Phase'] || ''
  const milestonePhasePrefix = resolveMilestonePhasePrefix(phaseName, msDelTypeId)

  const clItems     = ctx.clCache?.[msId]
  const passengerItems = ctx.msPassengerCache?.[msId] || null
  const bimItems    = isBimMs ? (ctx.bimCache || null) : null
  const sheetItems  = ctx.sheetCache?.[msId] || null
  // Done = QC-approved (ctx.qcSummaryCache is keyed by Sheet Revision id, which sheetItems already is).
  const sheetsProgress = Array.isArray(sheetItems) ? {
    done: sheetItems.filter(s => {
      const sm = ctx.qcSummaryCache?.[s.id]
      return !!(sm && (sm.approved || sm.status === 'approved'))
    }).length,
    total: sheetItems.length,
  } : null
  const conceptItems = (isConceptBranchMs || isDapcDrawingsMs) ? (ctx.conceptCache || null) : null
  // Permits branch (Mount A): project-scoped, unfiltered by milestone linkage — the
  // linkage a review carries (AddAgencyReviewModal's milestone picker) only controls
  // which milestone(s) get a heads-up/actionable presence; it was never meant to hide
  // the rest of the project's permits from a milestone that needs general permit access
  // (2026-07 — see docs/PERMIT_TRACKER.md). ctx.permitCache/permitReviewCache/
  // permitDeltaCache are owned + kept fresh by the always-mounted PermitsSection.jsx;
  // this is a read-only reuse of that same cache, not a second fetch.
  const msPermits       = isPermitMs ? (ctx.permitCache || null) : null
  const msBidDetails    = isBidMs ? (ctx.msBidCampaignCache?.[msId] ?? null) : null
  const msVeEfforts     = isVeMs ? (ctx.msVeCache?.[msId] ?? null) : null

  const catNodes = clItems ? ctx.getCategoryNodes(msId, clItems) : null
  // Aggregate done/total across all categories, for the Checklist header's own progress bar.
  const clProgress = catNodes ? {
    done: catNodes.reduce((sum, co) => sum + co.items.filter(i => i.isComplete).length, 0),
    total: catNodes.reduce((sum, co) => sum + co.items.length, 0),
  } : null

  // Discipline of a milestone sheet-revision: prefer the joined Drawing Sheet meta, else prefix detection.
  function discOfSheet(s) {
    const sid = getFirst(s.fields['Drawing Sheet']) || s.id
    const m = ctx.sheetDiscCache?.[sid]
    if (m && m.discipline) return m.discipline
    return getDiscipline((getFirst(s.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim())
  }

  // Open the QC tool at the first sheet in the set (discipline order, then sort order).
  function openFirstSheetQC() {
    if (!sheetItems || !sheetItems.length) return
    const order = DISCIPLINE_ORDER || []
    const titleOf = s => (getFirst(s.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim()
    const sortOf  = s => (getFirst(s.fields['Sheet Sort Order (from Drawing Sheet)']) || '').trim()
    const first = [...sheetItems].sort((a, b) => {
      const da = order.indexOf(discOfSheet(a)), db = order.indexOf(discOfSheet(b))
      const ka = sortOf(a), kb = sortOf(b)
      const bySort = !ka && !kb ? 0 : !ka ? 1 : !kb ? -1 : ka.localeCompare(kb, undefined, { numeric: true, sensitivity: 'base' })
      return (da < 0 ? 999 : da) - (db < 0 ? 999 : db) || bySort || titleOf(a).localeCompare(titleOf(b))
    })[0]
    const rawTitle = titleOf(first); const dashIdx = rawTitle.indexOf(' - ')
    const sheet = {
      id: getFirst(first.fields['Drawing Sheet']) || first.id,
      revisionId: first.id, revision: first, title: rawTitle || 'Untitled',
      sheetNumber: dashIdx !== -1 ? rawTitle.slice(0, dashIdx).trim() : rawTitle.trim(),
      sheetTitle:  dashIdx !== -1 ? rawTitle.slice(dashIdx + 3).trim() : '',
      progress: '', slackThreadTs: ctx.sheetTsCache?.[getFirst(first.fields['Drawing Sheet'])] || '',
    }
    openQcTab(sheet, milestonePhasePrefix, msId)
  }

  // Expand/collapse all disciplines in the Drawing Sheets section
  const sheetsDiscKeyPrefix = 'sec:' + msId + ':disc'
  function sheetsExpandAll() {
    if (!sheetItems) return
    const discs = [...new Set(sheetItems.map(discOfSheet))]
    discs.forEach(d => { wf.collapsed[sheetsDiscKeyPrefix + ':' + d] = false })
    window.dispatchEvent(new Event('outline-render'))
  }
  function sheetsCollapseAll() {
    if (!sheetItems) return
    const discs = [...new Set(sheetItems.map(discOfSheet))]
    discs.forEach(d => { wf.collapsed[sheetsDiscKeyPrefix + ':' + d] = true })
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <>
      {/* ASI moved under Documents (Matt, 2026-09-18) — see MilestoneDocumentsSection, which
          renders MilestoneAsiMount as its first child instead of here at the top of the list. */}

      {/* RFI / Submittal — same reverse-mount pattern as ASI (docs/PASSENGERS.md: RFIs and
          Submittals run as milestones now too). Renders null if this milestone has no linked
          item yet (e.g. was created manually via AddMilestoneWizard). */}

      {/* Feeder — when it went out, when it's expected back, what came back (docs/OPERATING_MODEL.md
          step 3). Renders nothing unless this milestone's type is set as a feeder. */}
      <MilestoneFeederSection msId={msId} readOnly={readOnly} />

      {/* Gate — what must be in hand before this milestone starts (sheet 3, docs/GATES.md).
          Renders nothing for the 49 types without one. Sits above the Checklist because it comes first. */}
      <MilestoneGateSection msId={msId} readOnly={kitReadOnly} />

      {/* Checklist */}
      <SectionNode msId={msId} type="checklist" label="Checklist" progress={clProgress}>
        {!clItems && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span>
              </div>
            </div>
          </div>
        )}
        {clItems && catNodes && catNodes.length === 0 && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No checklist items for this phase.</span>
              </div>
            </div>
          </div>
        )}
        {/* Items render directly under Checklist — no per-category node to click through
            (Matt, 2026-09-18). catNodes still groups/sorts by CAT_SORT_ORDER internally
            (Execution before Wrap-Up); that ordering is preserved here, just flattened. */}
        {catNodes && catNodes.flatMap(co => co.items).map(item => <ItemNode key={item.record.id} item={item} msId={msId} readOnly={readOnly} />)}
      </SectionNode>

      {/* Threads — threads riding this milestone as passengers (docs/PASSENGERS.md). Always shown,
          even at zero, so it reads as "you can pair a thread here" rather than only appearing once
          one already is — a passenger blocks posting, so seeing it here (not just on the Flight
          Board) is what makes that block legible. */}
      <SectionNode msId={msId} type="threads-riding" label="Threads" count={passengerItems ? passengerItems.length : null}
        menuContent={<>
          {Array.isArray(passengerItems) && passengerItems.length > 0 && <>
            <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-move-threads-modal', { detail: { projectId, fromMilestoneId: msId } }))}>Move threads to another milestone…</ActionMenuItem>
            <ActionMenuDivider />
          </>}
          <ActionMenuItem onClick={() => ctx.expandCollapseDescendants(sectionKey(msId, 'threads-riding'), true)}>Expand all</ActionMenuItem>
          <ActionMenuItem onClick={() => ctx.expandCollapseDescendants(sectionKey(msId, 'threads-riding'), false)}>Collapse all</ActionMenuItem>
        </>}>
        {!passengerItems && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span>
              </div>
            </div>
          </div>
        )}
        {Array.isArray(passengerItems) && passengerItems.length === 0 && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No threads riding this milestone</span>
              </div>
            </div>
          </div>
        )}
        {Array.isArray(passengerItems) && passengerItems.map(t => (
          <ThreadNode key={t.id} item={t} projectId={projectId} projectName={ms?.fields?.['Project Name'] || ''} />
        ))}
      </SectionNode>

      {/* Key Dates — calendar flags for the crew (docs/KEY_DATES.md), not milestones/work. */}
      <KeyDatesSection msId={msId} projectId={projectId} />

      {/* Documents — integrated (app-generated) + manual uploads, coord-item document pattern */}
      <MilestoneDocumentsSection msId={msId} ms={ms} readOnly={readOnly} />

      {/* Working Notes — free-form dated team context, not a checklist/task. Same canonical
          component ThreadNode.jsx mounts (see WorkingNotesSection.jsx). */}
      <WorkingNotesSection entityType="milestone" entityId={msId} />

      {/* Criteria — mounted under Pre-design Research and Conceptual Framework milestones
          (CRITERIA_BRANCH_MS_TYPE_IDS; Design > Criteria branch) */}
      {CRITERIA_BRANCH_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1 && <CriteriaSection msId={msId} readOnly={readOnly} />}
      {/* Parcels — mounted under Pre-design Research milestones (Design > Parcels branch) */}
      {msDelTypeId === PRE_DESIGN_RESEARCH_TYPE_ID && <ParcelsSection msId={msId} readOnly={readOnly} />}

      {/* Concepts — ICP, Conceptual Framework, and Integrated Design Proposal milestones
          (CONCEPT_BRANCH_MS_TYPE_IDS). Reuses the canonical ConceptRow from Reference > Design >
          Concepts (CLAUDE.md one-component-per-node-type) so expand/zoom/⋯ menu behave
          identically here. */}
      {isConceptBranchMs && (
        <SectionNode msId={msId} type="concepts" label="Concepts" count={conceptItems ? conceptItems.length : null}
          menuContent={<>
            <ActionMenuItem onClick={() => ctx.expandCollapseDescendants(sectionKey(msId, 'concepts'), true)}>Expand all</ActionMenuItem>
            <ActionMenuItem onClick={() => ctx.expandCollapseDescendants(sectionKey(msId, 'concepts'), false)}>Collapse all</ActionMenuItem>
          </>}
        >
          <ConceptsList concepts={conceptItems} recordId={projectId} hideAreaMetrics={hideAreaMetrics} ownerKey={sectionKey(msId, 'concepts')} readOnly={readOnly} />
        </SectionNode>
      )}

      {/* Design — Discretionary Approvals Plan Check Drawings milestones only.
          Shows the project's selected concept (Selected Concept = true).
          If none is selected, a prompt explains how to select one. */}
      {isDapcDrawingsMs && (
        <SectionNode msId={msId} type="design-concept" label="Design" count={null}>
          {!conceptItems && <div className="wf-loading">Loading…</div>}
          {conceptItems && (() => {
            const selected = conceptItems.find(c => c.fields['Selected Concept'] === true) || null
            if (!selected) {
              return (
                <div className="wf-node wf-node--item">
                  <div className="wf-row">
                    <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                    <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
                    <div className="wf-text-wrap">
                      <span className="wf-node-text" style={{ color: 'var(--aj-text-mid)', fontStyle: 'italic' }}>
                        Mark a concept as Selected in the Design section for it to appear here
                      </span>
                    </div>
                  </div>
                </div>
              )
            }
            return <ConceptRow key={selected.id} rec={selected} recordId={projectId} />
          })()}
        </SectionNode>
      )}

      {/* BIM Systems — BIM milestone types only (canonical BimRow, One-Component-Per-Node-Type) */}
      {bimItems && bimItems.length > 0 && (
        <SectionNode msId={msId} type="bim" label="BIM Systems" count={bimItems.length}>
          {bimItems.map(b => (
            <BimRow key={b.id} item={b} projectId={projectId} projectName={ms?.fields?.['Project Name'] || ''} restrictPhase={bimRestrictPhase} />
          ))}
        </SectionNode>
      )}

      {/* Mount A — Permits: shown on permit-related milestone types (not filtered by
          linkage — every project permit, since a milestone needing this branch at all
          needs general permit access, not just the ones a review happens to be linked to). */}
      {isPermitMs && (
        <SectionNode msId={msId} type="permits-ms" label="Permits" count={Array.isArray(msPermits) ? msPermits.length : null}>
          {msPermits === null && (
            <div className="wf-node wf-node--item"><div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>
            </div></div>
          )}
          {Array.isArray(msPermits) && msPermits.length === 0 && (
            <div className="wf-node wf-node--item"><div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No permits on this project yet.</span></div>
            </div></div>
          )}
          {Array.isArray(msPermits) && msPermits.map(permit => (
            <PermitNode key={permit.id} permit={permit} reviews={ctx.permitReviewCache} deltas={ctx.permitDeltaCache} />
          ))}
        </SectionNode>
      )}

      {/* Mount B — Bid Campaigns: shown on Bid Campaign / Bid Addendum milestone types */}
      {isBidMs && (
        <SectionNode msId={msId} type="bids-ms" label="Bid Campaigns" count={Array.isArray(msBidDetails) ? msBidDetails.length : null}>
          {msBidDetails === null && (
            <div className="wf-node wf-node--item"><div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>
            </div></div>
          )}
          {Array.isArray(msBidDetails) && msBidDetails.length === 0 && (
            <div className="wf-node wf-node--item"><div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No bid campaigns linked.</span></div>
            </div></div>
          )}
          {Array.isArray(msBidDetails) && msBidDetails.map(({ campaign, bids }) => {
            const sc = campaign.status === 'open' ? '#1D9E75' : campaign.status === 'closed' ? '#7a9aaa' : '#2563eb'
            const low = bids.length ? bids.reduce((m, b) => Number(b.price_normalized) < Number(m.price_normalized) ? b : m, bids[0]) : null
            return (
              <div key={campaign.id} className="wf-node wf-node--item">
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-bullet wf-bullet--item"></span>
                  <div className="wf-text-wrap">
                    <span className="wf-node-text" style={{ fontWeight: 600 }}>
                      Bidding Campaign No. {campaign.round_number ?? '—'}
                    </span>
                    <span className="wf-meta">
                      <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{campaign.status}</span>
                      {bids.length > 0 && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{bids.length} bid{bids.length !== 1 ? 's' : ''}</span>}
                      {low && low.price_normalized != null && (
                        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#1D9E75' }}>
                          low ${Number(low.price_normalized).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </SectionNode>
      )}

      {isVeMs && (
        <SectionNode msId={msId} type="ve-ms" label="Value Engineering" count={Array.isArray(msVeEfforts) ? msVeEfforts.length : null}>
          {msVeEfforts === null && (
            <div className="wf-node wf-node--item"><div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>
            </div></div>
          )}
          {Array.isArray(msVeEfforts) && msVeEfforts.length === 0 && (
            <div className="wf-node wf-node--item"><div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No VE efforts linked.</span></div>
            </div></div>
          )}
          {Array.isArray(msVeEfforts) && msVeEfforts.map(effort => {
            const sc = effort.status === 'complete' ? '#1D9E75' : effort.status === 'under_review' ? '#d97706' : '#2563eb'
            const acc = sumAcceptedSavings(effort)
            const items = effort.ve_items || []
            return (
              <div key={effort.id} className="wf-node wf-node--item">
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <button className="wf-bullet wf-bullet--item" title="Zoom into VE effort"
                    onClick={e => { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection('ve-effort:' + effort.id, { root: 'construction' }) }}></button>
                  <div className="wf-text-wrap">
                    <span className="wf-node-text" style={{ fontWeight: 600 }}>VE Effort No. {effort.effort_number ?? '—'}</span>
                    <span className="wf-meta">
                      <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{VE_STATUS_LABELS[effort.status] || effort.status}</span>
                      {items.length > 0 && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>}
                      {acc > 0 && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#1D9E75' }}>${Number(acc).toLocaleString('en-US', { maximumFractionDigits: 0 })} accepted</span>}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </SectionNode>
      )}

      {/* Drawing Sheets — shown for any drawing milestone (milestonePhasePrefix resolved, e.g. PPC
          Drawings Permit Revision) even with no sheets yet, so sheets can be added/pulled — plus
          whenever revisions already exist. isRevisionMs covers POST_REVISION_TYPE_IDS explicitly
          since milestonePhasePrefix is null for those by design (see resolveMilestonePhasePrefix)
          and would otherwise hide the section on a fresh revision milestone with no sheets yet —
          the exact case with no other way to add a first sheet. Never shown on spec milestones.
          Never shown on BIM Coordination milestones, full stop — no "still show if items exist"
          exception either, unlike isNoSheetsMs's softer suppression: BIM Coordination milestones
          should never carry drawing-sheet revisions at all (see docs/TITLEBLOCK_ISSUANCE.md's
          base-walk incident, 2026-07-22 — a sheet's Original Release getting mis-tagged to a BIM
          Coordination milestone broke titleblock resolution firm-wide). Hiding the section here
          removes the UI path that invites relinking a sheet there again. */}
      {!isSpecMs && !isBimMs && (((milestonePhasePrefix != null || isRevisionMs) && !isNoSheetsMs) || (Array.isArray(sheetItems) && sheetItems.length > 0)) && (
        <SectionNode msId={msId} type="sheets" label="Drawing Sheets" count={Array.isArray(sheetItems) ? sheetItems.length : 0}
          progress={sheetsProgress}
          menuContent={<>
            {Array.isArray(sheetItems) && sheetItems.length > 0 && (
              <ActionMenuItem onClick={openFirstSheetQC}>Open AlderQC</ActionMenuItem>
            )}
            <ActionMenuDivider />
            <ActionMenuItem onClick={sheetsExpandAll}>Expand all</ActionMenuItem>
            <ActionMenuItem onClick={sheetsCollapseAll}>Collapse all</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('enter-select-sheets', {
              detail: { collapseKeyPrefix: sheetsDiscKeyPrefix }
            }))}>
              Select sheets for export
            </ActionMenuItem>
            {/* AT-8Y7XZ: a preview set of the drawings uploaded so far, before the milestone is
                posted or the sheets are approved — stamped PREVIEW / NOT ISSUED on every page. */}
            {Array.isArray(sheetItems) && sheetItems.length > 0 && (
              <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('enter-select-sheets', {
                detail: { collapseKeyPrefix: sheetsDiscKeyPrefix, preview: true }
              }))}>
                Export preview of in-progress drawings
              </ActionMenuItem>
            )}
            {!readOnly && <ActionMenuDivider />}
            {!readOnly && (
              <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-add-sheets', {
                detail: {
                  msId,
                  ...(is3DMModel ? { allowedDisciplines: ['As-Built'], newSheetsOnly: true } : {}),
                },
              }))}>
                Add drawing sheets
              </ActionMenuItem>
            )}
          </>}
        >
          {!Array.isArray(sheetItems)
            ? msSheetFiller('Loading sheets…')
            : sheetItems.length === 0
            ? msSheetFiller(is3DMModel ? 'No as-built sheets yet — use ⋯ to add sheets.' : 'No drawing sheets yet — use ⋯ to add or pull sheets forward.')
            : <DrawingSheetList
            sheets={sheetItems.map(s => {
              // Combined lookup title — parse into number/title for inline-edit fields.
              // Format is "A1.1 - Site Plan" (number, space-dash-space, title).
              // Split on ' - ' to avoid the "-" appearing in the title portion.
              const rawTitle  = (getFirst(s.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim()
              const dashIdx   = rawTitle.indexOf(' - ')
              const sheetNumber = dashIdx !== -1 ? rawTitle.slice(0, dashIdx).trim() : rawTitle.trim()
              const sheetTitle  = dashIdx !== -1 ? rawTitle.slice(dashIdx + 3).trim() : ''
              const sid = getFirst(s.fields['Drawing Sheet']) || s.id
              const meta = ctx.sheetDiscCache?.[sid] || {}
              return {
                // Use the Drawing Sheet record ID (multipleRecordLinks → reliable per Airtable rules).
                // Fall back to revision ID only if the field is somehow absent.
                id:             sid,
                revisionId:     s.id,
                revision:       s,
                title:          rawTitle || 'Untitled',
                sheetNumber,
                sheetTitle,
                progress:       '',
                slackThreadTs:  ctx.sheetTsCache?.[sid] || '',
                templateId:     meta.templateId || '',
                insertTypeName: meta.insertTypeName || '',
                // Grouping + ordering (discipline comes from the joined sheet meta; sort orders from the revision).
                discipline:     meta.discipline || '',
                disciplineSort: meta.disciplineSort,
                sortOrder:      getFirst(s.fields['Drawing Sort Order from Drawing Sheet']) || getFirst(s.fields['Sheet Sort Order (from Drawing Sheet)']),
              }
            })}
            collapseKeyPrefix={'sec:' + msId + ':disc'}
            context="milestone"
            milestonePhasePrefix={milestonePhasePrefix}
            currentMemberId={wf.currentMemberId}
            milestoneId={msId}
            readOnly={readOnly}
            projectId={ctx.recordId || wf.zoomProjectId || null}
            projectName={ms?.fields?.['Project Name'] || ''}
            onDisciplineZoom={discipline => navigateToMsSection(msId, 'sec:' + msId + ':disc:' + discipline)}
            onSheetZoom={sheetId => navigateToMsSection(msId, 'sec:' + msId + ':sheet:' + sheetId)}
          />}
        </SectionNode>
      )}

      {/* Specifications — the drawing-sheet model (Round 18): the sections that belong to this
          milestone (have a version here). Shown for spec-bearing milestone types even when empty so
          the team can open the editor and add/pull sections. The editor opens scoped to this milestone. */}
      {(() => {
        const msRec = (ctx.state?.milestones || []).find(m => m.id === msId)
        const msDelTypeId = msRec ? (getFirst(msRec.fields?.['Deliverable']) || '') : ''
        const isSpecMs = Object.prototype.hasOwnProperty.call(SPEC_RELEASE_MS_TYPE_IDS, msDelTypeId)
        const specVers = ctx.msSpecVersionCache?.[msId]
        if (isSelectionsDocsMs || selectionsDocKinds(msDelTypeId).length) return null
        if (!isSpecMs && !(specVers && specVers.length)) return null
        function openEditor() {
          // AlderSPEC Phase 2 — opens the tool in its own tab, deep-linked to this milestone's
          // release (?msId= focuses the Releases tab). The page self-resolves the Selected concept,
          // so no concept plumbing is needed here.
          const pid = ctx.recordId || wf.zoomProjectId || ''
          window.open('/spec?projectId=' + encodeURIComponent(pid)
            + '&msId=' + encodeURIComponent(msId)
            + (ctx.projectName ? '&projectName=' + encodeURIComponent(ctx.projectName) : ''), '_blank')
        }
        const specMenu = <ActionMenuItem onClick={openEditor}>Open AlderSPEC</ActionMenuItem>
        if (!specVers || !specVers.length) {
          return (
            <SectionNode msId={msId} type="spec-versions" label="Specifications" count={0} menuContent={specMenu}>
              <div className="wf-node wf-node--item">
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                  <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
                  <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No spec sections yet — use ⋯ Open AlderSPEC.</span></div>
                </div>
              </div>
            </SectionNode>
          )
        }
        return (
          <SectionNode msId={msId} type="spec-versions" label="Specifications" count={specVers.length} menuContent={specMenu}>
            {specVers.map(v => specVersionRowNode(v))}
          </SectionNode>
        )
      })()}

      {/* Schedules — the spec model (Round 18): the items that belong to this milestone (have a
          version here). Shown for schedule-bearing milestone types even when empty so the team can
          open the editor and add/pull items. The editor opens scoped to this milestone + mode. */}
      {(() => {
        const msRec = (ctx.state?.milestones || []).find(m => m.id === msId)
        const msDelTypeId = msRec ? (getFirst(msRec.fields?.['Deliverable']) || '') : ''
        const isScheduleMs = Object.prototype.hasOwnProperty.call(SCHEDULE_RELEASE_MS_TYPE_IDS, msDelTypeId)
        const schedItems = ctx.msScheduleCache?.[msId]
        if (isSelectionsDocsMs || selectionsDocKinds(msDelTypeId).length) return null
        if (!isScheduleMs && !(schedItems && schedItems.length)) return null
        function openEditor() {
          // AlderSPEC Phase 4 — the Schedule Builder is AlderSPEC's Schedules tab now. Resolve the
          // project from the milestone's OWN link first (reliable in every view; the ctx.state
          // lookup is empty in the multi-project home). The page self-resolves the Selected concept.
          const mr = msRec || ms
          const projectId = getFirst(mr?.fields?.['Project'])
            || getFirst(mr?.fields?.['Project Record UID from Project'])
            || ctx.recordId || wf.zoomProjectId || ''
          const projectName = getFirst(mr?.fields?.['Project Name']) || ctx.projectName || ''
          window.open('/spec?projectId=' + encodeURIComponent(projectId)
            + '&msId=' + encodeURIComponent(msId) + '&tab=schedules'
            + (projectName ? '&projectName=' + encodeURIComponent(projectName) : ''), '_blank')
        }
        const schedMenu = <ActionMenuItem onClick={openEditor}>Open AlderSPEC (Schedules)</ActionMenuItem>
        if (!schedItems || !schedItems.length) {
          return (
            <SectionNode msId={msId} type="schedule-versions" label="Schedules" count={0} menuContent={schedMenu}>
              <div className="wf-node wf-node--item">
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                  <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
                  <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No schedule items yet — use ⋯ Open schedule editor.</span></div>
                </div>
              </div>
            </SectionNode>
          )
        }
        return (
          <SectionNode msId={msId} type="schedule-versions" label="Schedules" count={schedItems.length} menuContent={schedMenu}>
            {schedItems.map(it => scheduleMembershipRowNode(it))}
          </SectionNode>
        )
      })()}
    </>
  )
}

/* ── Section-zoom data loader (zoom mode only) ───────────────────────────── */
// Loads all milestone sub-section data when navigating to a milestone in zoom mode.
// Checks caches directly — no per-instance refs since caches guard against double-fetch.
function loadZoomMsData(msId) {
  if (!msId) return
  const ms = (ctx.state?.milestones || []).find(m => m.id === msId)
  const projectId = ctx.recordId || wf.zoomProjectId || null
  const msDelTypeId = ms ? (getFirst(ms.fields['Deliverable']) || '') : ''
  const isBimMs   = BIM_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
  const isConceptBranchMs = CONCEPT_BRANCH_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
  const isDapcDrawingsMs = msDelTypeId === DAPC_DRAWINGS_MS_TYPE_ID
  function dispatch() { window.dispatchEvent(new Event('outline-render')) }

  if (ctx.clCache && ctx.clCache[msId] === undefined) {
    ctx.clCache[msId] = null
    getMilestoneChecklistItems(msId)
      .then(r => { if (ctx.clCache) { ctx.clCache[msId] = r; dispatch() } })
      .catch(() => { if (ctx.clCache) { ctx.clCache[msId] = []; dispatch() } })
  }
  // Threads riding this milestone as passengers (docs/PASSENGERS.md) — same read PostMilestoneModal
  // uses to block posting; shown here so a paired thread is visible without the Flight Board.
  loadMsPassengerThreads(msId, dispatch)
  // Spec section versions at this milestone (zoom mode loader)
  if (!ctx.msSpecVersionCache) ctx.msSpecVersionCache = {}
  if (ctx.msSpecVersionCache[msId] === undefined) {
    ctx.msSpecVersionCache[msId] = null
    getMilestoneSpecVersions(msId)
      .then(vs => { if (ctx.msSpecVersionCache) { ctx.msSpecVersionCache[msId] = vs; dispatch() } })
      .catch(() => { if (ctx.msSpecVersionCache) { ctx.msSpecVersionCache[msId] = []; dispatch() } })
  }
  // Schedule items at this milestone (zoom mode loader)
  if (!ctx.msScheduleCache) ctx.msScheduleCache = {}
  if (ctx.msScheduleCache[msId] === undefined) {
    ctx.msScheduleCache[msId] = null
    getMilestoneScheduleItems(msId)
      .then(rows => { if (ctx.msScheduleCache) { ctx.msScheduleCache[msId] = rows; dispatch() } })
      .catch(() => { if (ctx.msScheduleCache) { ctx.msScheduleCache[msId] = []; dispatch() } })
  }

  if (ctx.sheetCache && ctx.sheetCache[msId] === undefined) {
    ctx.sheetCache[msId] = null
    getMilestoneSheetRevisions(msId)
      .then(rawRecords => {
        const seen = {}, sheets = []
        for (let i = 0; i < rawRecords.length; i++) {
          const sheetId = getFirst(rawRecords[i].fields['Drawing Sheet'])
                       || getFirst(rawRecords[i].fields['Record ID (from Drawing Sheet)'])
          if (sheetId && !seen[sheetId]) { seen[sheetId] = true; sheets.push(rawRecords[i]) }
        }
        sheets.sort(clientSheetSort)
        if (ctx.sheetCache) ctx.sheetCache[msId] = sheets
        // After deduplicating, fetch Slack Thread Timestamps from Drawing Sheet records.
        // Revision records don't carry this field — it lives on the Drawing Sheet itself.
        const sheetIds = sheets.map(r => getFirst(r.fields['Drawing Sheet'])).filter(Boolean)
        // loadBatch sets null sentinels atomically before starting any async work.
        // DrawingSheetList's bulk useEffect checks === undefined to decide whether to start —
        // seeing null here, it skips its own competing load. DataLoader dispatches outline-render
        // when results arrive.
        if (sheetIds.length > 0) {
          ctx.loader.loadBatch(sheetIds, id => 'sheetCl:' + id,
            ids => getProjectSheetChecklistItems(ids))
        }
        dispatch()
        if (sheetIds.length > 0) {
          getSheetSlackTimestamps(sheetIds)
            .then(tsRecords => {
              if (!ctx.sheetTsCache) ctx.sheetTsCache = {}
              tsRecords.forEach(r => {
                ctx.sheetTsCache[r.id] = r.fields?.['Slack Thread Timestamp'] || ''
              })
              dispatch()
            })
            .catch(() => {}) // non-fatal — indicator simply stays dark
        }
        // Discipline + discipline-sort live on the Drawing Sheet (the revision lookups don't carry them),
        // so load the project sheet meta once and key it by sheet id — drives correct grouping/order.
        const pid = ctx.recordId || wf.zoomProjectId
        if (pid && ctx.sheetDiscCacheProj !== pid) {
          ctx.sheetDiscCacheProj = pid
          ctx.sheetDiscCache = {}
          getProjectSheets(pid).then(rs => {
            (rs || []).forEach(r => {
              const f = r.fields || {}
              const disc = Array.isArray(f['Discipline']) ? f['Discipline'][0] : f['Discipline']
              const insertTypeArr = f['PDF Insert Type (from Drawing Sheet Template)']
              ctx.sheetDiscCache[r.id] = {
                discipline:     (disc || '').trim(),
                disciplineSort: f['Sheet Discipline Sort Order'],
                sortOrder:      f['Drawing Sort Order'] || f['Sheet Sort Order'],
                templateId:     (Array.isArray(f['Drawing Sheet Template']) ? f['Drawing Sheet Template'][0] : f['Drawing Sheet Template']) || '',
                insertTypeName: Array.isArray(insertTypeArr) ? (insertTypeArr[0] || '') : (insertTypeArr || ''),
              }
            })
            dispatch()
          }).catch(() => {})
        }
      })
      .catch(() => { if (ctx.sheetCache) { ctx.sheetCache[msId] = []; dispatch() } })
  }
  // NOTE: The secondary checklist fetch that used to live here (lines ~901-917) has been
  // removed. DataLoader's deduplication covers this case: if sheets were already cached
  // but checklists weren't loaded, DrawingSheetList's bulk useEffect or per-sheet
  // maybeLoadChecklist will call ctx.loader.loadBatch/load, which deduplicates automatically.
  // Load organizations (for coord item External Organization display).
  // Missing from this function was the root cause of org names never showing in zoom mode.
  if (!ctx.orgsCache) {
    ctx.orgsCache = [] // in-flight sentinel ([] is truthy — prevents re-entry)
    getOrganizations()
      .then(list => { ctx.orgsCache = list; dispatch() })
      .catch(() => { ctx.orgsCache = []; dispatch() })
  }
  if (isBimMs && projectId && ctx.bimCache === null) {
    ctx.bimCache = []
    getProjectBimSystems(projectId)
      .then(r => { ctx.bimCache = r; dispatch() })
      .catch(() => { ctx.bimCache = []; dispatch() })
  }
  if ((isConceptBranchMs || isDapcDrawingsMs) && projectId && ctx.conceptCache === null) {
    ctx.conceptCache = []
    getConcepts(projectId)
      .then(r => { ctx.conceptCache = r; dispatch() })
      .catch(() => { ctx.conceptCache = []; dispatch() })
  }
  // Spec section versions issued at this milestone (drawing-sheet parallel):
  // only the sections that were "passed through" or changed at this milestone.
  if (!ctx.msSpecVersionCache) ctx.msSpecVersionCache = {}
  if (ctx.msSpecVersionCache[msId] === undefined) {
    ctx.msSpecVersionCache[msId] = null   // in-flight sentinel
    getMilestoneSpecVersions(msId)
      .then(vs => { if (ctx.msSpecVersionCache) { ctx.msSpecVersionCache[msId] = vs; dispatch() } })
      .catch(() => { if (ctx.msSpecVersionCache) { ctx.msSpecVersionCache[msId] = []; dispatch() } })
  }

  if (ctx.docCache && ctx.docCache[msId] === undefined) {
    ctx.docCache[msId] = null
    getMilestoneDocuments(msId)
      .then(res => { if (ctx.docCache) { ctx.docCache[msId] = res.documents || []; dispatch() } })
      .catch(() => { if (ctx.docCache) { ctx.docCache[msId] = []; dispatch() } })
  }
  // Mount A — permits: no fetch needed here. ctx.permitCache/permitReviewCache/
  // permitDeltaCache are project-scoped and owned by the always-mounted
  // PermitsSection.jsx, which loads/resets them on project change; this component
  // only reads them (see msPermits in MsContent).
  // Mount B — bid campaigns under Bid Campaign / Bid Addendum milestones
  const isBidMs = msDelTypeId === BID_CAMPAIGN_MS_TYPE_ID || msDelTypeId === BID_ADDENDUM_MS_TYPE_ID
  if (isBidMs) {
    if (!ctx.msBidCampaignCache) ctx.msBidCampaignCache = {}
    if (ctx.msBidCampaignCache[msId] === undefined) {
      ctx.msBidCampaignCache[msId] = null
      getBidCampaignForMilestone(msId)
        .then(r => { if (ctx.msBidCampaignCache) { ctx.msBidCampaignCache[msId] = r; dispatch() } })
        .catch(() => { if (ctx.msBidCampaignCache) { ctx.msBidCampaignCache[msId] = []; dispatch() } })
    }
  }
  // Mount B — value engineering efforts under Value Engineering milestones
  if (msDelTypeId === VALUE_ENGINEERING_MS_TYPE_ID) {
    if (!ctx.msVeCache) ctx.msVeCache = {}
    if (ctx.msVeCache[msId] === undefined) {
      ctx.msVeCache[msId] = null
      getEffortsForMilestone(msId)
        .then(r => { if (ctx.msVeCache) { ctx.msVeCache[msId] = r; dispatch() } })
        .catch(() => { if (ctx.msVeCache) { ctx.msVeCache[msId] = []; dispatch() } })
    }
  }
}

/* ── Section zoom renderer — one section type, no outer SectionNode wrapper ─ */
function MsSectionZoom({ msId, sectionType, readOnly = false }) {
  const ms = (ctx.state?.milestones || []).find(m => m.id === msId)
  readOnly = readOnly || !!(ms?.fields?.['Posted Date'])
  const kitReadOnly = readOnly                       // the kit stays open while blocked (see MsContent)
  readOnly = readOnly || (ms ? isBlocked(ms) : false)

  if (sectionType === 'sheets') {
    const sheetItems = ctx.sheetCache?.[msId]
    const phaseName  = ms?.fields?.['Project Phase'] || ''
    const msTypeName = getFirst(ms?.fields?.['Name (from Deliverable)']) || ''
    const milestonePhasePrefix = resolveMilestonePhasePrefix(phaseName, msTypeName)
    if (sheetItems === null || sheetItems === undefined) {
      return <div className="wf-list"><div className="wf-loading">Loading sheets...</div></div>
    }
    if (sheetItems.length === 0) {
      return <div className="wf-list"><div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No drawing sheets</div></div>
    }
    const msForZoom = (ctx.state?.milestones || []).find(m => m.id === msId)
    return (
      <div className="wf-list">
        <DrawingSheetList
          sheets={sheetItems.map(s => {
            const rawTitle = (getFirst(s.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim()
            const dashIdx  = rawTitle.indexOf(' - ')
            const sheetNumber = dashIdx !== -1 ? rawTitle.slice(0, dashIdx).trim() : rawTitle.trim()
            const sheetTitle  = dashIdx !== -1 ? rawTitle.slice(dashIdx + 3).trim() : ''
            const sid = getFirst(s.fields['Drawing Sheet']) || s.id
            const meta = ctx.sheetDiscCache?.[sid] || {}
            return {
              id:             sid,
              revisionId:     s.id,
              revision:       s,
              title:          rawTitle || 'Untitled',
              sheetNumber,
              sheetTitle,
              progress:       '',
              slackThreadTs:  ctx.sheetTsCache?.[sid] || '',
              templateId:     meta.templateId || '',
              insertTypeName: meta.insertTypeName || '',
              discipline:     meta.discipline || '',
              disciplineSort: meta.disciplineSort,
              sortOrder:      getFirst(s.fields['Drawing Sort Order from Drawing Sheet']) || getFirst(s.fields['Sheet Sort Order (from Drawing Sheet)']),
            }
          })}
          collapseKeyPrefix={'sec:' + msId + ':disc'}
          context="milestone"
          milestonePhasePrefix={milestonePhasePrefix}
          currentMemberId={wf.currentMemberId}
          milestoneId={msId}
          readOnly={readOnly}
          projectId={ctx.recordId || wf.zoomProjectId || null}
          projectName={msForZoom?.fields?.['Project Name'] || ''}
          onDisciplineZoom={discipline => navigateToMsSection(msId, 'sec:' + msId + ':disc:' + discipline)}
          onSheetZoom={sheetId => navigateToMsSection(msId, 'sec:' + msId + ':sheet:' + sheetId)}
        />
      </div>
    )
  }

  if (sectionType === 'checklist') {
    const clItems  = ctx.clCache?.[msId]
    const catNodes = clItems ? ctx.getCategoryNodes(msId, clItems) : null
    return (
      <div className="wf-list">
        {!clItems && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span>
              </div>
            </div>
          </div>
        )}
        {clItems && catNodes && catNodes.length === 0 && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No checklist items for this phase.</span>
              </div>
            </div>
          </div>
        )}
        {/* Items render directly, no per-category node — see the identical inline-mode note above. */}
        {catNodes && catNodes.flatMap(co => co.items).map(item => <ItemNode key={item.record.id} item={item} msId={msId} readOnly={readOnly} />)}
      </div>
    )
  }

  if (sectionType === 'threads-riding') {
    const passengerItems = ctx.msPassengerCache?.[msId] || null
    const projectId = getFirst(ms?.fields?.['Project']) || ctx.recordId || wf.zoomProjectId || ''
    return (
      <div className="wf-list">
        {!passengerItems && <div className="wf-loading">Loading…</div>}
        {Array.isArray(passengerItems) && passengerItems.length === 0 && <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No threads riding this milestone</div>}
        {Array.isArray(passengerItems) && passengerItems.map(t => (
          <ThreadNode key={t.id} item={t} projectId={projectId} projectName={ms?.fields?.['Project Name'] || ''} />
        ))}
      </div>
    )
  }

  if (sectionType === 'bim') {
    const msDelTypeId = getFirst(ms?.fields?.['Deliverable']) || ''
    const isBimMs   = BIM_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
    const bimItems  = isBimMs ? ctx.bimCache : null
    const bimZoomRestrictPhase = BIM_MS_TYPE_PHASE[msDelTypeId] || null
    const projectId = getFirst(ms?.fields?.['Project']) || ctx.recordId || wf.zoomProjectId || ''
    return (
      <div className="wf-list">
        {!bimItems && <div className="wf-loading">Loading BIM systems...</div>}
        {Array.isArray(bimItems) && bimItems.length === 0 && <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No BIM systems</div>}
        {Array.isArray(bimItems) && bimItems.map(b => (
          <BimRow key={b.id} item={b} projectId={projectId} projectName={ms?.fields?.['Project Name'] || ''} restrictPhase={bimZoomRestrictPhase} />
        ))}
      </div>
    )
  }

  if (sectionType === 'concepts') {
    const conceptItems = ctx.conceptCache
    const projectId = getFirst(ms?.fields?.['Project']) || ctx.recordId || wf.zoomProjectId || ''
    const zoomMsDelTypeId = getFirst(ms?.fields?.['Deliverable']) || ''
    const zoomHideAreaMetrics = zoomMsDelTypeId === CONCEPTUAL_FRAMEWORK_TYPE_ID || zoomMsDelTypeId === INTEGRATED_DESIGN_PROPOSAL_TYPE_ID || BIM_MS_TYPE_IDS.indexOf(zoomMsDelTypeId) !== -1
    return (
      <div className="wf-list">
        <ConceptsList concepts={conceptItems} recordId={projectId} hideAreaMetrics={zoomHideAreaMetrics} ownerKey={sectionKey(msId, 'concepts')} readOnly={readOnly} />
      </div>
    )
  }

  if (sectionType === 'design-concept') {
    const conceptItems = ctx.conceptCache
    const projectId = getFirst(ms?.fields?.['Project']) || ctx.recordId || wf.zoomProjectId || ''
    const selected = Array.isArray(conceptItems)
      ? conceptItems.find(c => c.fields['Selected Concept'] === true) || null
      : null
    return (
      <div className="wf-list">
        {!conceptItems && <div className="wf-loading">Loading…</div>}
        {Array.isArray(conceptItems) && !selected && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
              <div className="wf-text-wrap">
                <span className="wf-node-text" style={{ color: 'var(--aj-text-mid)', fontStyle: 'italic' }}>
                  Mark a concept as Selected in the Design section for it to appear here
                </span>
              </div>
            </div>
          </div>
        )}
        {selected && <ConceptRow rec={selected} recordId={projectId} />}
      </div>
    )
  }

  if (sectionType === 'documents') {
    return <MilestoneDocumentsSection msId={msId} readOnly={readOnly} sectionZoom />
  }

  if (sectionType === 'gate') {
    return <MilestoneGateSection msId={msId} readOnly={kitReadOnly} sectionZoom />
  }

  if (sectionType === 'feeder') {
    return <MilestoneFeederSection msId={msId} readOnly={readOnly} sectionZoom />
  }

  if (sectionType === 'criteria') {
    return <CriteriaSection msId={msId} readOnly={readOnly} sectionZoom />
  }

  if (sectionType === 'parcels') {
    return <ParcelsSection msId={msId} readOnly={readOnly} sectionZoom />
  }

  if (sectionType === 'spec-versions') {
    const specVers = ctx.msSpecVersionCache?.[msId]
    return (
      <div className="wf-list">
        {specVers == null && <div className="wf-loading">Loading specifications…</div>}
        {Array.isArray(specVers) && specVers.length === 0 && (
          <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No spec sections yet — open AlderSPEC from the ⋯ menu.</div>
        )}
        {Array.isArray(specVers) && specVers.map(v => specVersionRowNode(v))}
      </div>
    )
  }

  if (sectionType === 'schedule-versions') {
    const schedItems = ctx.msScheduleCache?.[msId]
    return (
      <div className="wf-list">
        {schedItems == null && <div className="wf-loading">Loading schedules…</div>}
        {Array.isArray(schedItems) && schedItems.length === 0 && (
          <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No schedule items yet — open the schedule editor from the ⋯ menu.</div>
        )}
        {Array.isArray(schedItems) && schedItems.map(it => scheduleMembershipRowNode(it))}
      </div>
    )
  }

  return null
}

/* ── Main export ─────────────────────────────────────────────────────────── */
// msId + ms props = inline mode (rendered inside MilestoneRow, not zoomed)
//   ms prop is the milestone record passed from the parent — needed for BIM/ICP type detection
//   when ctx.state.milestones may not contain this record (home level, multiple projects)
// no props = zoom mode (rendered in OutlineView when wf.zoomMsId is set)
export default function MilestoneSections({ msId: propMsId, ms: propMs, readOnly: propReadOnly = false } = {}) {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  // Track wf.zoomMsId as state so the zoom-mode data-loading useEffect fires on navigation
  const [zoomMsId, setZoomMsId] = useState(() => wf.zoomMsId || null)

  // Lazy-load refs — one per data type, prevent duplicate fetches across re-renders
  const clLoaded       = useRef(false)
  const passengersLoaded = useRef(false)
  const sheetsLoaded   = useRef(false)
  const bimLoaded      = useRef(false)
  const conceptsLoaded = useRef(false)
  const docsLoaded     = useRef(false)
  const bidsLoaded     = useRef(false)
  const veLoaded       = useRef(false)
  const specVersLoaded = useRef(false)
  const scheduleLoaded = useRef(false)

  useEffect(() => {
    function onRender() {
      forceUpdate()
      // Keep zoomMsId in sync so zoom-mode data-load useEffect fires on navigation
      setZoomMsId(prev => {
        const next = wf.zoomMsId || null
        return prev !== next ? next : prev
      })
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  // Reload the milestone's schedule membership when the editor (or item modal) changes it.
  useEffect(() => {
    function onMembership() {
      const ids = []
      if (propMsId) ids.push(propMsId)
      if (wf.zoomMsId) ids.push(wf.zoomMsId)
      if (!ids.length) return
      if (!ctx.msScheduleCache) ctx.msScheduleCache = {}
      ids.forEach(id => {
        getMilestoneScheduleItems(id)
          .then(rows => { if (ctx.msScheduleCache) { ctx.msScheduleCache[id] = rows; window.dispatchEvent(new Event('outline-render')) } })
          .catch(() => {})
      })
    }
    window.addEventListener('schedule-membership-changed', onMembership)
    return () => window.removeEventListener('schedule-membership-changed', onMembership)
  }, [propMsId])

  // Lazy-load all milestone sub-section data when in inline mode.
  // Fires on mount (handles page refresh with node already expanded) and whenever
  // propMsId changes. Uses the same ctx cache sentinel pattern as the outline engine.
  useEffect(() => {
    if (!propMsId) return
    const msId = propMsId
    const ms = propMs

    // Derive the current project ID without a prop — works for both single-project
    // page (ctx.recordId from URL) and home multi-project view (wf.zoomProjectId).
    const projectId = ctx.recordId || wf.zoomProjectId || null

    const msDelTypeId = ms ? (getFirst(ms.fields['Deliverable']) || '') : ''
    const isBimMs   = BIM_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
    const isConceptBranchMs = CONCEPT_BRANCH_MS_TYPE_IDS.indexOf(msDelTypeId) !== -1
    const isDapcDrawingsMs = msDelTypeId === DAPC_DRAWINGS_MS_TYPE_ID
    const isSpecMs  = Object.prototype.hasOwnProperty.call(SPEC_RELEASE_MS_TYPE_IDS, msDelTypeId)
    const isScheduleMs = Object.prototype.hasOwnProperty.call(SCHEDULE_RELEASE_MS_TYPE_IDS, msDelTypeId)

    function dispatch() { window.dispatchEvent(new Event('outline-render')) }

    // ── Checklist ─────────────────────────────────────────────────────────
    // ctx.clCache uses undefined = not started, [] / [...] = loaded (no null sentinel).
    if (!clLoaded.current && ctx.clCache?.[msId] === undefined) {
      clLoaded.current = true
      getMilestoneChecklistItems(msId)
        .then(records => { if (ctx.clCache) ctx.clCache[msId] = records; dispatch() })
        .catch(() => { if (ctx.clCache) ctx.clCache[msId] = []; dispatch() })
    } else {
      clLoaded.current = true // already in cache (loaded by outline engine on zoom)
    }

    // ── Threads riding this milestone as passengers (docs/PASSENGERS.md) ──
    if (!passengersLoaded.current) {
      passengersLoaded.current = true
      loadMsPassengerThreads(msId, dispatch)
    }

    // ── Organizations (project-scoped, for External Organization display) ─
    // ctx.orgsCache: null/undefined = not started, [] = loading/empty, [...] = loaded.
    if (!ctx.orgsCache) {
      ctx.orgsCache = [] // in-flight sentinel
      getOrganizations()
        .then(list => { ctx.orgsCache = list; dispatch() })
        .catch(() => { ctx.orgsCache = []; dispatch() })
    }

    // ── Sheet revisions ────────────────────────────────────────────────────
    // ctx.sheetCache[msId]: undefined = not started, null = in-flight, [] / [...] = loaded.
    // Spec milestones and agency-submission milestones never show drawing sheets — skip fetch.
    const isNoSheetsMs = isNoSheetsMsType(msDelTypeId)
    if (!isSpecMs && !isNoSheetsMs && !sheetsLoaded.current && ctx.sheetCache?.[msId] === undefined) {
      sheetsLoaded.current = true
      if (ctx.sheetCache) ctx.sheetCache[msId] = null // in-flight sentinel
      getMilestoneSheetRevisions(msId)
        .then(rawRecords => {
          // Deduplicate by drawing sheet ID — keeps first revision per sheet.
          // Use Drawing Sheet (multipleRecordLinks) for reliable IDs per Airtable API rules.
          const seen = {}, sheets = []
          for (let i = 0; i < rawRecords.length; i++) {
            const sheetId = getFirst(rawRecords[i].fields['Drawing Sheet'])
                         || getFirst(rawRecords[i].fields['Record ID (from Drawing Sheet)'])
            if (sheetId && !seen[sheetId]) { seen[sheetId] = true; sheets.push(rawRecords[i]) }
          }
          sheets.sort(clientSheetSort)
          if (ctx.sheetCache) ctx.sheetCache[msId] = sheets
          const inlineSheetIds = sheets.map(r => getFirst(r.fields['Drawing Sheet'])).filter(Boolean)
          // loadBatch sets null sentinels atomically before starting any async work,
          // so DrawingSheetList's bulk useEffect (which checks === undefined) skips competing loads.
          if (inlineSheetIds.length > 0) {
            ctx.loader.loadBatch(inlineSheetIds, id => 'sheetCl:' + id,
              ids => getProjectSheetChecklistItems(ids))
          }
          dispatch()
          if (inlineSheetIds.length > 0) {
            getSheetSlackTimestamps(inlineSheetIds)
              .then(tsRecords => {
                if (!ctx.sheetTsCache) ctx.sheetTsCache = {}
                tsRecords.forEach(r => { ctx.sheetTsCache[r.id] = r.fields?.['Slack Thread Timestamp'] || '' })
                dispatch()
              })
              .catch(() => {})
          }
        })
        .catch(() => { if (ctx.sheetCache) ctx.sheetCache[msId] = []; dispatch() })
    } else {
      sheetsLoaded.current = true
    }

    // Load project sheet metadata (insertTypeName, discipline, templateId) independently of
    // whether sheets were pre-loaded by the outline engine. On page refresh with nodes
    // already expanded, the outline engine calls loadSheets() before this useEffect fires,
    // setting sheetCache[msId]=null (in-flight). That makes the sheetCache===undefined check
    // above FALSE, skipping the .then() path entirely — so sheetDiscCache was never
    // populated and PDF Insert sections never rendered. Running this block unconditionally
    // (guarded only by sheetDiscCacheProj) ensures it always runs regardless of who loaded
    // the sheets first.
    const discPid = ctx.recordId || wf.zoomProjectId || null
    if (!isSpecMs && !isNoSheetsMs && discPid && ctx.sheetDiscCacheProj !== discPid) {
      ctx.sheetDiscCacheProj = discPid
      ctx.sheetDiscCache = {}
      getProjectSheets(discPid).then(rs => {
        ;(rs || []).forEach(r => {
          const f = r.fields || {}
          const disc = Array.isArray(f['Discipline']) ? f['Discipline'][0] : f['Discipline']
          const insertTypeArr = f['PDF Insert Type (from Drawing Sheet Template)']
          ctx.sheetDiscCache[r.id] = {
            discipline:     (disc || '').trim(),
            disciplineSort: f['Sheet Discipline Sort Order'],
            sortOrder:      f['Drawing Sort Order'] || f['Sheet Sort Order'],
            templateId:     (Array.isArray(f['Drawing Sheet Template']) ? f['Drawing Sheet Template'][0] : f['Drawing Sheet Template']) || '',
            insertTypeName: Array.isArray(insertTypeArr) ? (insertTypeArr[0] || '') : (insertTypeArr || ''),
          }
        })
        dispatch()
      }).catch(() => {})
    }

    // ── BIM systems (project-scoped) ───────────────────────────────────────
    // ctx.bimCache: null = not started, [] = in-flight or loaded empty, [...] = loaded.
    if (isBimMs && projectId && !bimLoaded.current && ctx.bimCache === null) {
      bimLoaded.current = true
      ctx.bimCache = [] // in-flight sentinel (same as loadBim)
      getProjectBimSystems(projectId)
        .then(records => { ctx.bimCache = records; dispatch() })
        .catch(() => { ctx.bimCache = []; dispatch() })
    } else if (isBimMs) {
      bimLoaded.current = true
    }

    // ── Project concepts (project-scoped, CONCEPT_BRANCH_MS_TYPE_IDS + DAPC Drawings milestones) ──
    // ctx.conceptCache: null = not started, [] = in-flight or loaded empty, [...] = loaded.
    if ((isConceptBranchMs || isDapcDrawingsMs) && projectId && !conceptsLoaded.current && ctx.conceptCache === null) {
      conceptsLoaded.current = true
      ctx.conceptCache = [] // in-flight sentinel (same as loadConcepts)
      getConcepts(projectId)
        .then(records => { ctx.conceptCache = records; dispatch() })
        .catch(() => { ctx.conceptCache = []; dispatch() })
    } else if (isConceptBranchMs || isDapcDrawingsMs) {
      conceptsLoaded.current = true
    }

    // ── Documents ─────────────────────────────────────────────────────────
    // ctx.docCache[msId]: undefined = not started, null = in-flight, [] / [...] = loaded.
    if (!docsLoaded.current && ctx.docCache?.[msId] === undefined) {
      docsLoaded.current = true
      if (ctx.docCache) ctx.docCache[msId] = null // in-flight sentinel
      getMilestoneDocuments(msId)
        .then(res => { if (ctx.docCache) ctx.docCache[msId] = res.documents || []; dispatch() })
        .catch(() => { if (ctx.docCache) ctx.docCache[msId] = []; dispatch() })
    } else {
      docsLoaded.current = true
    }

    // ── Mount A: Permits — no fetch needed. Project-scoped caches owned by the
    // always-mounted PermitsSection.jsx; this component only reads them.

    // ── Mount B: Bid campaigns under Bid Campaign / Bid Addendum milestones ─
    const isBidMs = msDelTypeId === BID_CAMPAIGN_MS_TYPE_ID || msDelTypeId === BID_ADDENDUM_MS_TYPE_ID
    if (isBidMs && !bidsLoaded.current) {
      bidsLoaded.current = true
      if (!ctx.msBidCampaignCache) ctx.msBidCampaignCache = {}
      if (ctx.msBidCampaignCache[msId] === undefined) {
        ctx.msBidCampaignCache[msId] = null
        getBidCampaignForMilestone(msId)
          .then(r => { if (ctx.msBidCampaignCache) { ctx.msBidCampaignCache[msId] = r; dispatch() } })
          .catch(() => { if (ctx.msBidCampaignCache) { ctx.msBidCampaignCache[msId] = []; dispatch() } })
      }
    }

    // ── Spec section versions (spec-bearing milestones) ──────────────────────
    // Bug fix: previously only the ZOOM loader fetched these, so on a page load with the milestone
    // expanded inline (not zoomed) the Specifications section never populated until you zoomed in.
    if (isSpecMs && !specVersLoaded.current) {
      specVersLoaded.current = true
      if (!ctx.msSpecVersionCache) ctx.msSpecVersionCache = {}
      if (ctx.msSpecVersionCache[msId] === undefined) {
        ctx.msSpecVersionCache[msId] = null
        getMilestoneSpecVersions(msId)
          .then(vs => { if (ctx.msSpecVersionCache) { ctx.msSpecVersionCache[msId] = vs; dispatch() } })
          .catch(() => { if (ctx.msSpecVersionCache) { ctx.msSpecVersionCache[msId] = []; dispatch() } })
      }
    }

    // ── Schedule items at this milestone (schedule-bearing milestones) ───────
    if (isScheduleMs && !scheduleLoaded.current) {
      scheduleLoaded.current = true
      if (!ctx.msScheduleCache) ctx.msScheduleCache = {}
      if (ctx.msScheduleCache[msId] === undefined) {
        ctx.msScheduleCache[msId] = null
        getMilestoneScheduleItems(msId)
          .then(rows => { if (ctx.msScheduleCache) { ctx.msScheduleCache[msId] = rows; dispatch() } })
          .catch(() => { if (ctx.msScheduleCache) { ctx.msScheduleCache[msId] = []; dispatch() } })
      }
    }

    // ── Mount B: Value Engineering efforts under Value Engineering milestones ─
    if (msDelTypeId === VALUE_ENGINEERING_MS_TYPE_ID && !veLoaded.current) {
      veLoaded.current = true
      if (!ctx.msVeCache) ctx.msVeCache = {}
      if (ctx.msVeCache[msId] === undefined) {
        ctx.msVeCache[msId] = null
        getEffortsForMilestone(msId)
          .then(r => { if (ctx.msVeCache) { ctx.msVeCache[msId] = r; dispatch() } })
          .catch(() => { if (ctx.msVeCache) { ctx.msVeCache[msId] = []; dispatch() } })
      }
    }

  }, [propMsId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Zoom-mode data loading — fires when the user navigates to a different milestone zoom.
  // Uses loadZoomMsData which checks caches directly (no per-instance refs).
  useEffect(() => {
    if (propMsId) return  // inline mode uses the above effect
    if (!zoomMsId) return
    loadZoomMsData(zoomMsId)
  }, [propMsId, zoomMsId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh sheet revisions on post/unpost — ctx.sheetCache[msId] is a lazy-load-once cache
  // (guarded by sheetsLoaded.current in inline mode, by the cache sentinel alone in zoom mode),
  // so posting/unposting a milestone (which changes each revision's Posted Date — the exact field
  // the QC "Replace (Admin)" button's posted-state check reads) left this section showing stale
  // data until a hard refresh (Matt, 2026-07-23). PostMilestoneModal.jsx and outline.js's unpost
  // handler both include msId on the existing 'sheet-revisions-stale' event (previously only
  // sheetIds, for the separate Reference/Production ctx.sheetRevCache) — reusing the same event
  // rather than adding a new one. Duplicates the fetch/dedupe/sort already done in the two loaders
  // above (small, matches this file's existing pattern) since neither loader's own effect can be
  // re-triggered from here — dependency-array useEffects don't re-run just because a ref/cache was
  // cleared.
  useEffect(() => {
    function onSheetRevisionsStale(e) {
      const staleMsId = e.detail?.msId
      if (!staleMsId) return
      const isInline = propMsId && propMsId === staleMsId
      const isZoom = !propMsId && zoomMsId === staleMsId
      if (!isInline && !isZoom) return
      if (isInline) sheetsLoaded.current = false
      if (ctx.sheetCache) ctx.sheetCache[staleMsId] = null // in-flight sentinel
      getMilestoneSheetRevisions(staleMsId)
        .then(rawRecords => {
          const seen = {}, sheets = []
          for (let i = 0; i < rawRecords.length; i++) {
            const sheetId = getFirst(rawRecords[i].fields['Drawing Sheet'])
                         || getFirst(rawRecords[i].fields['Record ID (from Drawing Sheet)'])
            if (sheetId && !seen[sheetId]) { seen[sheetId] = true; sheets.push(rawRecords[i]) }
          }
          sheets.sort(clientSheetSort)
          if (ctx.sheetCache) ctx.sheetCache[staleMsId] = sheets
          window.dispatchEvent(new Event('outline-render'))
        })
        .catch(() => { if (ctx.sheetCache) ctx.sheetCache[staleMsId] = []; window.dispatchEvent(new Event('outline-render')) })
    }
    window.addEventListener('sheet-revisions-stale', onSheetRevisionsStale)
    return () => window.removeEventListener('sheet-revisions-stale', onSheetRevisionsStale)
  }, [propMsId, zoomMsId])

  // Compute read-only state:
  // - Inline mode: propReadOnly comes from MilestoneRow (which gets it from PhaseRow's isClosedOut)
  // - Zoom mode: look up the phase from wf.zoomPhaseId and check Phase Closed Out
  const zoomPhaseClosedOut = !propMsId && wf.zoomPhaseId
    ? (ctx.state?.phases || []).find(p => p.id === wf.zoomPhaseId)?.fields?.['Phase Closed Out'] === true
    : false
  const isReadOnly = propReadOnly || zoomPhaseClosedOut

  if (propMsId) {
    // Inline mode — render full section content for the given milestone
    return <MsContent msId={propMsId} ms={propMs} readOnly={isReadOnly} />
  }

  // Zoom mode — only show when zoomed into a milestone
  const msId = wf.zoomMsId
  if (!msId) return null
  if (wf.zoomCat || wf.zoomItemId || wf.searchQuery) return null

  // Section zoom within this milestone — render only the matching section
  if (wf.zoomSection && wf.zoomSection.startsWith('sec:' + msId + ':')) {
    const afterPrefix = wf.zoomSection.slice(('sec:' + msId + ':').length)
    const sectionType = afterPrefix.split(':')[0]  // 'sheets', 'checklist', 'threads-riding', 'bim', 'concepts', 'disc'

    if (sectionType === 'sheet') {
      // Sheet-level zoom within milestone: sec:{msId}:sheet:{sheetId} or :sheet:{sheetId}:cl
      const rest     = afterPrefix.slice('sheet:'.length)
      const colonIdx = rest.indexOf(':')
      const sheetId  = colonIdx !== -1 ? rest.slice(0, colonIdx) : rest
      const subKey   = colonIdx !== -1 ? rest.slice(colonIdx + 1) : null
      const sheetItems = ctx.sheetCache?.[msId]
      const ms = (ctx.state?.milestones || []).find(m => m.id === msId)
      const phaseName  = ms?.fields?.['Project Phase'] || ''
      const msTypeName = getFirst(ms?.fields?.['Name (from Deliverable)']) || ''
      const milestonePhasePrefix = resolveMilestonePhasePrefix(phaseName, msTypeName)
      const revRecord = Array.isArray(sheetItems)
        ? sheetItems.find(s => getFirst(s.fields['Drawing Sheet']) === sheetId)
        : null
      let targetSheet = null
      if (revRecord) {
        const rawTitle = (getFirst(revRecord.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim()
        const dashIdx  = rawTitle.indexOf(' - ')
        targetSheet = {
          id:             sheetId,
          revisionId:     revRecord.id,
          revision:       revRecord,
          title:          rawTitle || 'Untitled',
          sheetNumber:    dashIdx !== -1 ? rawTitle.slice(0, dashIdx).trim() : rawTitle.trim(),
          sheetTitle:     dashIdx !== -1 ? rawTitle.slice(dashIdx + 3).trim() : '',
          progress:       '',
          slackThreadTs:  ctx.sheetTsCache?.[sheetId] || '',
          insertTypeName: ctx.sheetDiscCache?.[sheetId]?.insertTypeName || '',
        }
      }
      return (
        <div className="wf-list">
          {(sheetItems === undefined || sheetItems === null) && <div className="wf-loading">Loading sheet...</div>}
          {Array.isArray(sheetItems) && !targetSheet && <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>Sheet not found</div>}
          {targetSheet && (
            <SheetRow
              sheet={targetSheet}
              context="milestone"
              milestonePhasePrefix={milestonePhasePrefix}
              currentMemberId={wf.currentMemberId}
              milestoneId={msId}
              readOnly={isReadOnly}
              zoomedAs={subKey === 'cl' ? 'cl' : 'sheet'}
              onSheetZoom={sk => navigateToMsSection(msId, 'sec:' + msId + ':sheet:' + sk)}
              projectId={ctx.recordId || wf.zoomProjectId || null}
              projectName={ms?.fields?.['Project Name'] || ''}
            />
          )}
        </div>
      )
    }

    if (sectionType === 'disc') {
      // Discipline zoom within milestone — show only that discipline's sheets
      const filterDiscipline = afterPrefix.slice('disc:'.length)
      const sheetItems = ctx.sheetCache?.[msId]
      const ms = (ctx.state?.milestones || []).find(m => m.id === msId)
      const phaseName = ms?.fields?.['Project Phase'] || ''
      const msTypeName = getFirst(ms?.fields?.['Name (from Deliverable)']) || ''
      const milestonePhasePrefix = resolveMilestonePhasePrefix(phaseName, msTypeName)
      return (
        <div className="wf-list">
          {(sheetItems === undefined || sheetItems === null) && <div className="wf-loading">Loading sheets...</div>}
          {Array.isArray(sheetItems) && (
            <DrawingSheetList
              sheets={sheetItems.map(s => {
                const rawTitle = (getFirst(s.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim()
                const dashIdx  = rawTitle.indexOf(' - ')
                const sheetNumber = dashIdx !== -1 ? rawTitle.slice(0, dashIdx).trim() : rawTitle.trim()
                const sheetTitle  = dashIdx !== -1 ? rawTitle.slice(dashIdx + 3).trim() : ''
                const sid = getFirst(s.fields['Drawing Sheet']) || s.id
                const meta = ctx.sheetDiscCache?.[sid] || {}
                return {
                  id:             sid,
                  revisionId:     s.id,
                  revision:       s,
                  title:          rawTitle || 'Untitled',
                  sheetNumber,
                  sheetTitle,
                  progress:       '',
                  slackThreadTs:  '',
                  templateId:     meta.templateId || '',
                  insertTypeName: meta.insertTypeName || '',
                }
              })}
              collapseKeyPrefix={'sec:' + msId + ':disc'}
              context="milestone"
              milestonePhasePrefix={milestonePhasePrefix}
              currentMemberId={wf.currentMemberId}
              milestoneId={msId}
              readOnly={isReadOnly}
              filterDiscipline={filterDiscipline}
              onSheetZoom={sheetId => navigateToMsSection(msId, 'sec:' + msId + ':sheet:' + sheetId)}
              projectId={ctx.recordId || wf.zoomProjectId || null}
              projectName={(ctx.state?.milestones || []).find(m => m.id === msId)?.fields?.['Project Name'] || ''}
            />
          )}
        </div>
      )
    }

    return <MsSectionZoom msId={msId} sectionType={sectionType} readOnly={isReadOnly} />
  }

  // Section zoom for a different context — bail
  if (wf.zoomSection) return null

  return (
    <div className="wf-list">
      <MsContent msId={msId} readOnly={isReadOnly} />
    </div>
  )
}
